import * as Sentry from '@sentry/vue'

import SoftphoneAPI from '@/api/softphone.api'
import {
  SOFTPHONE_EVENTS,
  createSoftphoneTransport,
  exposeSoftphoneDevHook,
  removeSoftphoneDevHook
} from '@/services/softphone'
import { createSoftphoneOwnership } from '@/services/softphone/softphoneOwnership'
import { armRingtone, startRinging, stopRinging } from '@/services/softphone/ringtone'
import { goOfflineWithKeepalive } from '@/services/softphone/goOfflineBeacon'
import { createSoftphoneChannel, SOFTPHONE_COMMANDS } from '@/services/softphone/softphoneChannel'
import { getReconnectDelayInMs, getReconnectPolicy } from '@/services/softphone/reconnectPolicy'
import { getHeartbeatPolicy } from '@/services/softphone/heartbeatPolicy'
import { AUDIO_DEVICE_FAILURES, appliedDeviceResult, failedDeviceResult } from '@/services/softphone/audioDevices'
import {
  loadAudioDevicePreferences,
  saveAudioDevicePreferences
} from '@/services/softphone/audioDevicePreferences'

/**
 * `otherTab` is not part of the call lifecycle: it is where this tab lands when another tab takes
 * the softphone over, so two tabs can never answer for the same agent.
 */
export const SOFTPHONE_STATUS = Object.freeze({
  OFFLINE: 'offline',
  CONNECTING: 'connecting',
  READY: 'ready',
  RINGING: 'ringing',
  ACTIVE: 'active',
  WRAP_UP: 'wrapUp',
  /**
   * The registration went away without the agent asking, and the widget is getting it back. Its own
   * status rather than a second meaning for `CONNECTING`: connecting is what the agent started by
   * pressing Go Live and what they are waiting for, this is unattended and there is nothing for
   * them to do about it - and the widget has to say which of the two it is, because the campaign is
   * paused here and will resume by itself.
   */
  RECONNECTING: 'reconnecting',
  /**
   * Registered, live for a campaign, and getting nothing - because the account's balance is below
   * the minimum it has to hold to buy calls, so Ringba caps the buyer and routes none of them here.
   *
   * READY's twin rather than a flag on top of it, because the whole failure this exists for is a
   * widget that said Ready through a silent shift. The agent has done nothing wrong and nothing is
   * broken, so it is not an `ERROR` either; it is the one state where the softphone is working
   * perfectly and still cannot ring, and only somebody adding funds ends it.
   *
   * Counts as ONLINE everywhere (see `ONLINE_STATUSES`): the registration is up, the campaign is
   * still live server-side, and a call that was already being routed can still land. Nothing here
   * pauses the campaign or drops the transport - a top-up takes the agent straight back to Ready
   * without them having to go live again.
   */
  BALANCE_TOO_LOW: 'balanceTooLow',
  /**
   * The same silence for the other reason: the account has spent the daily budget it set itself, so
   * Ringba caps the buyer until the day rolls over.
   *
   * Its own status rather than a second meaning for `BALANCE_TOO_LOW`, because what the agent is
   * meant to DO differs and it is the only thing they can act on. An empty balance sends them to
   * whoever holds the card; a spent budget is a number somebody on the account can raise, and it
   * comes back on its own at midnight either way. Telling them their balance is too low when the
   * account is merely capped for the day would send them chasing the wrong person.
   */
  BUDGET_REACHED: 'budgetReached',
  /**
   * Registered, and the campaign behind it was paused on the server by somebody other than this
   * widget - the system's auto-pause after too many unanswered calls, the heartbeat watchdog, or an
   * administrator. Ringba routes nothing to a paused campaign, so this is the same silent shift the
   * two statuses above exist to explain, with a different way out.
   *
   * A blocked status like them, and for the same reasons: the registration is up, nothing here has
   * failed, and the agent stays registered so that resuming takes them straight back to Ready.
   * Unlike them the agent can usually end it themselves, by resuming the campaign from the widget -
   * except for an administrator's strict pause, which only an administrator lifts.
   *
   * Seen only through the live campaign poll (see `refreshCampaignBudget`), so it lands up to one
   * poll interval after the pause did. The widget's own pauses - wrap-up, a reconnect - are told
   * apart from it there and never land here.
   */
  CAMPAIGN_PAUSED: 'campaignPaused',
  OTHER_TAB: 'otherTab',
  ERROR: 'error'
})

/**
 * The statuses that mean REGISTERED BUT UNABLE TO BUY CALLS. One list, so every surface that has to
 * treat the two alike - the badge colour, the header dot, the tab title, the notice in the panel -
 * asks instead of enumerating them and missing one when a third arrives.
 */
export const BLOCKED_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.BALANCE_TOO_LOW,
  SOFTPHONE_STATUS.BUDGET_REACHED,
  SOFTPHONE_STATUS.CAMPAIGN_PAUSED
])

/**
 * Which blocked status the account's limits resolve to, or null when it can buy calls.
 *
 * A PAUSED CAMPAIGN OUTRANKS BOTH. It is the one the agent can usually end on the spot, from the
 * widget, and resuming it is the step that has to happen whatever else is wrong; a balance or a
 * budget still standing afterwards shows up the moment the campaign is live again.
 *
 * The BALANCE OUTRANKS THE BUDGET, exactly as the dashboard banner orders the same two: a daily
 * budget resets at midnight on its own, and a balance below the account's minimum does not resolve
 * until somebody adds funds. One badge, so the agent is shown the one worth acting on.
 *
 * Takes the server's own flags and nothing else. Which minimum the account must hold and what it
 * has spent today are its business; recomputing either here would be a second, worse copy of that
 * rule. An absent flag is not a limit - a balance that has not loaded yet must never read as broke.
 */
export function resolveSoftphoneBlockedStatus({
  isBalanceLow,
  isDailyBudgetReached,
  isCampaignDailyBudgetReached,
  isCampaignPaused
} = {}) {
  if (isCampaignPaused === true) {
    return SOFTPHONE_STATUS.CAMPAIGN_PAUSED
  }

  if (isBalanceLow === true) {
    return SOFTPHONE_STATUS.BALANCE_TOO_LOW
  }

  // The account's budget before the campaign's: the account one stops EVERY campaign, so it is the
  // larger fact and the one that is still true after the agent moves to another campaign.
  if (isDailyBudgetReached === true || isCampaignDailyBudgetReached === true) {
    return SOFTPHONE_STATUS.BUDGET_REACHED
  }

  return null
}

/**
 * What the panel says under the badge, and where it sends the agent. Kept here beside the statuses
 * rather than in the widget, so the wording and the status it belongs to cannot drift apart.
 *
 * Each names the person who can end it. That is the whole point of splitting the two statuses: an
 * agent who reads "balance too low" goes to whoever holds the card, and one who reads "daily budget"
 * goes to whoever sets the budget - and being sent to the wrong one is a shift spent waiting.
 */
const BLOCKED_NOTICE_BUILDERS = {
  [SOFTPHONE_STATUS.BALANCE_TOO_LOW]: () => ({
    text: 'Your account balance is too low to buy calls, so none will reach you.',
    linkText: 'Add funds',
    routeName: 'AddFunds'
  }),

  /**
   * One status, two budgets, two different pages to go and change - so this one is built from the
   * limits rather than looked up. The ACCOUNT's budget wins the wording when both are spent, as it
   * does in `resolveSoftphoneBlockedStatus`: raising the campaign's would change nothing while the
   * account's is still capped.
   */
  [SOFTPHONE_STATUS.BUDGET_REACHED]: state => {
    if (state.callBuyingLimits.isDailyBudgetReached) {
      return {
        text: 'Your account has used up its daily budget, so no more calls will reach you today.',
        linkText: 'Change your daily budget',
        routeName: 'BuyerAccountBudget'
      }
    }

    return {
      text: 'This campaign has used up its daily budget, so no more calls will reach you today.',
      linkText: 'Change the campaign budget',
      routeName: 'BuyerEditSoftphoneCampaign',
      routeParams: { id: state.liveCampaign?.id }
    }
  },

  /**
   * An ACTION rather than a link: the way out is to resume the campaign, and the widget can do that
   * itself - see `requestResumePausedCampaign`. Not for a strict pause, which the agent's resume
   * would only be refused on; that one names the administrator and their reason instead.
   */
  [SOFTPHONE_STATUS.CAMPAIGN_PAUSED]: state => {
    if (state.callBuyingLimits.isCampaignStrictlyPaused) {
      const reason = state.callBuyingLimits.campaignStrictPauseReason

      return {
        text: reason
          ? `An administrator paused this campaign: ${reason}`
          : 'An administrator paused this campaign, so no calls will reach you.'
      }
    }

    return {
      text: 'Your campaign was paused, so no calls will reach you. This happens automatically after several unanswered calls.',
      actionText: 'Resume taking calls',
      action: 'requestResumePausedCampaign'
    }
  }
}

/**
 * Why a reconnect is running. Only the first wait differs: a socket that dropped is given the
 * backoff's first delay, which is also the window in which the SDK's own recovery can beat us to
 * it, while a token being replaced before it expires is nobody's failure and starts at once.
 */
export const RECONNECT_REASONS = Object.freeze({
  SOCKET_CLOSED: 'socketClosed',
  TOKEN_RENEWAL: 'tokenRenewal'
})

/**
 * Which tab this store is. The softphone lives in the header of every dashboard tab, but exactly
 * one of them OWNS the transport and may touch a call; the rest are MIRRORS that render the owner's
 * broadcasts and ask the owner to act. A store that has not been told which it is does neither.
 *
 * The role is not fixed for the life of a tab: a mirror inherits ownership when the owner's tab
 * goes away, and an owner becomes a mirror when the agent takes the softphone over somewhere else.
 * See `services/softphone/softphoneOwnership.js` for how the two are kept to exactly one.
 */
export const SOFTPHONE_ROLES = Object.freeze({
  OWNER: 'owner',
  MIRROR: 'mirror'
})

/**
 * Post-call wrap-up. An answered call that runs past WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS earns the
 * agent a breather: the widget pauses the live campaign server-side while the call is still up, and
 * once it ends holds WRAP_UP_DURATION_IN_MS before resuming the campaign. Shorter calls (and declines)
 * go straight back to Ready - the campaign was never paused, so there is nothing to wait for.
 *
 * The pause happens DURING the call rather than at hang-up so that a call already being routed as
 * the agent hangs up finds the campaign paused, not a widget that is about to stop answering.
 */
export const WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS = 15000
export const WRAP_UP_DURATION_IN_MS = 60000

// Matched to the header's balance poll deliberately: the two answer halves of one question, and a
// widget whose halves refresh on different clocks reports a cap that is half lifted.
export const CAMPAIGN_BUDGET_POLL_INTERVAL_IN_MS = 45000

const STATUS_LABELS = {
  [SOFTPHONE_STATUS.OFFLINE]: 'Offline',
  [SOFTPHONE_STATUS.CONNECTING]: 'Connecting',
  [SOFTPHONE_STATUS.READY]: 'Ready',
  [SOFTPHONE_STATUS.RINGING]: 'Incoming',
  [SOFTPHONE_STATUS.ACTIVE]: 'On call',
  [SOFTPHONE_STATUS.WRAP_UP]: 'Wrap-up',
  [SOFTPHONE_STATUS.RECONNECTING]: 'Reconnecting',
  // Shorter than the state it names, and deliberately: this label is also the tiny line under the
  // phone icon in the header, where anything longer than Reconnecting widens the header itself. The
  // sentence that says which balance and what to do about it belongs in the panel, which has room
  // for it - see SoftphoneWidget.
  [SOFTPHONE_STATUS.BALANCE_TOO_LOW]: 'Balance low',
  [SOFTPHONE_STATUS.BUDGET_REACHED]: 'Budget spent',
  [SOFTPHONE_STATUS.CAMPAIGN_PAUSED]: 'Paused',
  [SOFTPHONE_STATUS.OTHER_TAB]: 'Other tab',
  [SOFTPHONE_STATUS.ERROR]: 'Error'
}

/**
 * The badge colour each status wears, for the surfaces that render the status as a Bootstrap badge
 * rather than with the widget's own CSS. As close to the widget's palette as the variants get:
 * green is taking calls, yellow is live but not this second, grey is not taking calls at all.
 *
 * `light` for a call in progress, matching the widget's own On call badge, which is the one it
 * deliberately leaves the colour of the panel behind it - the agent is already on the call and does
 * not need to be told about it in colour.
 */
const STATUS_BADGE_VARIANTS = {
  [SOFTPHONE_STATUS.OFFLINE]: 'secondary',
  [SOFTPHONE_STATUS.CONNECTING]: 'warning',
  [SOFTPHONE_STATUS.READY]: 'success',
  [SOFTPHONE_STATUS.RINGING]: 'info',
  [SOFTPHONE_STATUS.ACTIVE]: 'light',
  [SOFTPHONE_STATUS.WRAP_UP]: 'warning',
  // Yellow with the wrap-up and connecting: live but not taking calls this second, and on its way
  // back. Deliberately not `danger` - nothing has failed yet, and a red badge on every Wi-Fi hop
  // teaches the agent to ignore red.
  [SOFTPHONE_STATUS.RECONNECTING]: 'warning',
  // Red, with the error, and not the yellow above it. Yellow says live but not this second and on
  // its way back by itself, which is exactly what this is not: no amount of waiting fixes it, and
  // the agent needs to be sent to whoever holds the card. The only status in the list that asks
  // for something outside the widget.
  [SOFTPHONE_STATUS.BALANCE_TOO_LOW]: 'danger',
  // Red with the balance rather than yellow, even though the budget does come back on its own at
  // midnight. Yellow in this widget means back in a moment and nothing to do about it; a day is not
  // a moment, and somebody on the account can raise the number now.
  [SOFTPHONE_STATUS.BUDGET_REACHED]: 'danger',
  // Red with the other two: it does not come back by itself, and the agent has something to do.
  [SOFTPHONE_STATUS.CAMPAIGN_PAUSED]: 'danger',
  [SOFTPHONE_STATUS.OTHER_TAB]: 'secondary',
  [SOFTPHONE_STATUS.ERROR]: 'danger'
}

/**
 * The widget's own wording, for anything outside the widget that has to say the same thing. An
 * unknown status reads Offline: the widget is not taking calls, which is the truth for every state
 * this store does not have a word for.
 */
export function getSoftphoneStatusLabel(status) {
  return STATUS_LABELS[status] || STATUS_LABELS[SOFTPHONE_STATUS.OFFLINE]
}

export function getSoftphoneStatusBadgeVariant(status) {
  return STATUS_BADGE_VARIANTS[status] || STATUS_BADGE_VARIANTS[SOFTPHONE_STATUS.OFFLINE]
}

// Transport, ownership lock and timers are deliberately module scoped: they are neither
// serialisable nor reactive, and keeping them out of state means strict mode never sees them mutate.
let transport = null
let ownership = null
let wrapUpTimerId = null
let wrapUpEligibilityTimerId = null

/**
 * Re-reads the LIVE campaign's own daily budget for as long as the shift runs.
 *
 * Its own poll because nothing else can tell the widget: the account's two flags ride the buyer
 * balance endpoint, which the header already refreshes, but a campaign's budget state exists only
 * on the single-campaign endpoint - the list returns the entity, which has no such field. Without
 * this, a campaign that spent its budget mid-shift left the agent on a green Ready badge, which is
 * exactly the failure the blocked statuses exist for.
 *
 * One campaign, one request, same cadence as the balance beside it.
 */
let campaignBudgetPollTimerId = null

/**
 * Which campaign-budget read is the newest one asked for.
 *
 * The interval does not await the read it starts, so a read slower than the 45 second tick is still
 * in flight when the next goes out - and whichever ANSWERS last would otherwise win. A stale `false`
 * landing after a fresh `true` puts the badge back to Ready on a campaign that is still capped,
 * which is the exact lie this whole thing exists to stop, and it would stand until the next tick.
 *
 * Monotonic for the life of the module and deliberately never reset: a counter that restarted with
 * the shift could hand a new read the same number as one still in flight from the old one.
 */
let campaignBudgetRequestId = 0

/**
 * A resume of a campaign somebody else paused is in flight. Module scoped rather than on the
 * button, because a mirror's button re-enables the moment its command is posted: repeated clicks
 * there would otherwise stack Go Live requests in the owner, each one another chance to race the
 * agent going offline.
 */
let isResumingPausedCampaign = false

/**
 * A wrap-up pause the call earned but the network refused to take.
 *
 * The eligibility timer fires once per call and is cleared when it does, so a pause that failed
 * used to be the end of it - and the failure that matters is not the server saying no, it is the
 * agent's connection being down, which is exactly when the reconnect is about to put it back. A
 * lid closed mid-call therefore cost the agent the break the call had already earned, silently,
 * with only a red message left behind to say so.
 */
let isWrapUpPauseOwed = false

/**
 * The call whose ending has already been handled, so the SDK's second report of it is recognised
 * as a repeat - see `handleCallEnded`. Call ids are unique, so this is never cleared between
 * calls; it is only reset with the module.
 */
let endedCallId = null

/**
 * The reconnect in flight, or null. One object per run rather than a handful of flags, because
 * every cancellation point - going offline, losing the tab lock, unloading, the SDK recovering by
 * itself - has to stop exactly the run it was looking at and not a later one that started while it
 * was awaiting something.
 *
 * `{ attempt, isCancelled, hasFinished, resumeOnSuccess, restoreStatus, timerId, resolveWait }`.
 * The loop checks `isCancelled` after every await; `resolveWait` is how a cancellation wakes a run
 * that is sitting in a backoff instead of leaving the timer to expire.
 */
let reconnectRun = null

/**
 * A reconnect that is owed but must not start yet, because a call is on. Rebuilding the client
 * under a live call would hang up on the caller to fix the socket, so the work is remembered and
 * `handleCallEnded` starts the loop once the call is over.
 *
 * It holds the REASON rather than a flag, because the two reasons are owed for opposite conditions
 * and only one of them is a broken registration. A `TOKEN_RENEWAL` deferral is sitting on a healthy
 * socket, so a drop arriving during that call still has to be acted on - `handleUnregistered`
 * upgrades it, and a flag would have swallowed exactly the drop the campaign pause exists for.
 */
let deferredReconnectReason = null

/**
 * The bound on a deferral that is waiting for a call to end over a socket that has already gone.
 *
 * `handleCallEnded` is driven by the SDK's `telnyx.notification`, which travels over that socket,
 * and `keepConnectionAliveOnSocketClose` stops the SDK tearing the call down by itself - so a far
 * end that hangs up during the outage cannot tell this tab, and the deferral would wait for an
 * event that is never coming. Only armed for a DROP; a token renewal is deferred behind a call
 * whose signalling still works.
 */
let deferredReconnectTimerId = null

let tokenRenewalTimerId = null

// The heartbeat interval, running for as long as the widget is genuinely registered - the same
// moments `tokenRenewalTimerId` is armed for, since both represent "this registration is real
// right now." A `setInterval`, not a chain of `setTimeout`s like the reconnect backoff: the
// interval is fixed, so there is nothing to recompute between ticks.
let heartbeatTimerId = null

// Set for the duration of `goOffline`. Both transports emit UNREGISTERED from their own
// `disconnect()`, and that event now takes the agent offline itself - without this flag the
// teardown would re-enter itself and pause the campaign a second time.
let isGoingOffline = false
let channel = null

// The teardown in flight, if any. Logging out and unloading can both ask for it, and a teardown
// whose pause request fails can trigger a logout of its own - so a second request joins the first
// rather than starting over with a transport the first is still disconnecting.
let teardownPromise = null
let accessGeneration = 0

// The channel's handlers are append-only, and a tab changes role at runtime, so they are registered
// once per channel and each one checks the role it is for. Registering them per role transition
// would stack a duplicate handler on every handover.
let areChannelHandlersRegistered = false

export function getSoftphoneChannel() {
  return channel
}

function clearWrapUpTimer() {
  if (wrapUpTimerId) {
    clearTimeout(wrapUpTimerId)
    wrapUpTimerId = null
  }
}

/**
 * Forgets the wrap-up the current call is earning, whether it is still being earned (the timer) or
 * has been earned and is waiting on a server that would not take it (the owed pause).
 *
 * Both belong to ONE call and neither may follow the agent into the next, so they are dropped
 * together - which is what puts the owed pause on every path that ends a call or a shift without
 * each of them having to remember it.
 */
function clearWrapUpEligibilityTimer() {
  isWrapUpPauseOwed = false

  if (wrapUpEligibilityTimerId) {
    clearTimeout(wrapUpEligibilityTimerId)
    wrapUpEligibilityTimerId = null
  }
}

function _clearTokenRenewalTimer() {
  if (tokenRenewalTimerId) {
    clearTimeout(tokenRenewalTimerId)
    tokenRenewalTimerId = null
  }
}

function _clearHeartbeatTimer() {
  if (heartbeatTimerId) {
    clearInterval(heartbeatTimerId)
    heartbeatTimerId = null
  }
}

/**
 * A backoff wait that can be woken. The loop awaits this; a cancellation resolves it immediately
 * so the loop reaches its next `isCancelled` check now rather than when the timer would have run -
 * which is what makes Go Offline during a 15 second backoff feel like a click rather than a wish.
 */
function _waitForReconnectDelay(run, delayInMs) {
  return new Promise(resolve => {
    const settle = () => {
      run.timerId = null
      run.resolveWait = null

      resolve()
    }

    run.resolveWait = settle
    run.timerId = setTimeout(settle, delayInMs)
  })
}

function _clearDeferredReconnect() {
  deferredReconnectReason = null

  if (deferredReconnectTimerId) {
    clearTimeout(deferredReconnectTimerId)
    deferredReconnectTimerId = null
  }
}

/**
 * Remembers a reconnect that is owed until the call ends, and - for a drop - bounds the wait.
 *
 * The timeout dispatches `handleCallEnded` rather than starting the run directly, because the call
 * this tab believes it is on is over in every sense that matters: its signalling went with the
 * socket, so it can be neither ended by the far end nor transferred, and leaving it on screen would
 * hold the widget in a state with no exit but Hang up. Settling it through the ordinary path is
 * what then starts the run, with the status the call left behind.
 */
function _deferReconnect(reason, dispatch) {
  _clearDeferredReconnect()

  deferredReconnectReason = reason

  if (reason !== RECONNECT_REASONS.SOCKET_CLOSED) {
    return
  }

  deferredReconnectTimerId = setTimeout(() => {
    deferredReconnectTimerId = null

    dispatch('handleCallEnded')
  }, getReconnectPolicy().deferredReconnectTimeoutInMs)
}

/**
 * Stops the reconnect, whether it is waiting, attempting, or only owed. Safe to call when there is
 * none: every teardown path calls it unconditionally rather than asking first.
 */
function _cancelReconnect() {
  _clearDeferredReconnect()

  const run = reconnectRun

  if (!run) {
    return
  }

  reconnectRun = null
  run.isCancelled = true

  if (run.timerId) {
    clearTimeout(run.timerId)
    run.timerId = null
  }

  if (run.resolveWait) {
    run.resolveWait()
  }
}

/**
 * The token endpoint refusing the agent rather than failing to answer. There is nothing to retry:
 * the session behind the request is gone, and every further attempt would be refused the same way
 * while the agent watches a widget that looks busy.
 */
function _isSessionGoneError(error) {
  const status = error?.response?.status

  return status === 401 || status === 403
}

/**
 * `RECONNECTING` counts as online: the agent's SHIFT is still on - they have a live campaign and
 * they have not asked to stop - and it is what keeps Go Offline alive while the widget is getting
 * the registration back. An agent who has given up waiting must be able to end the shift, and a
 * dead Go Offline button would leave them nothing to press but the backoff.
 */
const ONLINE_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.CONNECTING,
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP,
  SOFTPHONE_STATUS.RECONNECTING,
  // Both count as online for the same reason RECONNECTING does, and more so: the registration is
  // up, the campaign is live, and the shift is on. It is what keeps Go Offline alive so an agent
  // whose account has been capped can end their shift, rather than watching a red badge.
  ...BLOCKED_STATUSES
])

const IN_CALL_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE
])

/**
 * Registered, and between calls: no call ringing, none up, and no wrap-up running.
 *
 * The two of them are one state as far as the call lifecycle is concerned - the difference is only
 * whether the account can pay for what arrives - so anything asking "is the agent sitting waiting
 * for a call" has to accept either. A call CAN still land on `BALANCE_TOO_LOW`: one already being
 * routed when the cap landed, or one arriving in the seconds between a top-up and the next poll.
 */
const READY_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.READY,
  ...BLOCKED_STATUSES
])

/**
 * The states a lost registration is worth reconnecting from: a shift that was REGISTERED and is
 * now not. `CONNECTING` is deliberately absent - a drop there belongs to the Go Live the agent is
 * standing in front of, `goOnline` reports it as a failed go-live, and a reconnect racing that
 * would fight the agent's own next click.
 */
const RECONNECTABLE_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.READY,
  SOFTPHONE_STATUS.RINGING,
  SOFTPHONE_STATUS.ACTIVE,
  SOFTPHONE_STATUS.WRAP_UP,
  // A registration lost while the account is capped is worth getting back like any other. The cap
  // is the buyer's problem and a top-up or a raised budget can land at any moment; an agent who
  // also lost their shift to a Wi-Fi hop meanwhile would have to go live again to find out.
  ...BLOCKED_STATUSES
])

/**
 * Whether a patch says anything the limits do not already say.
 *
 * Both sources re-report the same answer every 45 seconds for the whole of a shift, and the limits
 * ride the broadcast snapshot - so committing an unchanged patch posts a full state message to
 * every other tab, and each mirror then replaces its `liveCampaign`, `incomingCall`, `activeCall`
 * and limits with fresh objects and re-renders. Mid-call included. Cheap to not do.
 */
function _isKnownCallBuyingLimits(current, patch) {
  return Object.keys(patch).every(key => current[key] === patch[key])
}

/**
 * The statuses in which the WIDGET ITSELF may be holding the campaign paused: a reconnect pauses it
 * until the registration is back, and a go-live in progress has not resumed it yet. A paused
 * campaign read in any of these says nothing about anybody else pausing it.
 */
const SELF_PAUSING_STATUSES = Object.freeze([
  SOFTPHONE_STATUS.OFFLINE,
  SOFTPHONE_STATUS.CONNECTING,
  SOFTPHONE_STATUS.RECONNECTING,
  SOFTPHONE_STATUS.OTHER_TAB,
  SOFTPHONE_STATUS.ERROR
])

/**
 * Whether a paused campaign could be this widget's own doing right now. The wrap-up pauses it from
 * 15 seconds into a long call until the wrap-up's resume has landed, and a reconnect pauses it for
 * as long as it runs. Only a pause that none of these explain was somebody else's.
 */
function _isWidgetHoldingCampaignPaused(state) {
  return state.isPausedForWrapUp
    || state.isEndingWrapUp
    // A drop mid-call: the status stays ACTIVE for the call's sake, but the reconnect has paused
    // the campaign behind it until `finishDeferredReconnect` resumes it.
    || state.isRestoringConnection
    || SELF_PAUSING_STATUSES.includes(state.status)
}

const NO_CAMPAIGN_PAUSE = Object.freeze({
  isCampaignPaused: false,
  isCampaignStrictlyPaused: false,
  campaignStrictPauseReason: null
})

/**
 * The widget has just resumed the campaign itself - after a wrap-up, a reconnect, or the agent's
 * resume - so whatever pause the poll last saw is over. Cleared here rather than left for the next
 * poll, which is up to 45 seconds away: the Ready this resume is about to ask for would otherwise
 * resolve to Paused for that long, on a campaign that is live.
 *
 * Bumps the request id too, so a read that went out while the campaign was still paused and
 * answers after this cannot put the pause back.
 *
 * And moves the badge off Paused if it is already there - a widget that reached Ready while the
 * pause was recorded is showing it, and the next poll will not move it: its answer matches the
 * limits cleared here, so it returns before re-applying anything.
 */
function _forgetCampaignPause({ commit, dispatch, state }) {
  campaignBudgetRequestId++

  if (_isKnownCallBuyingLimits(state.callBuyingLimits, NO_CAMPAIGN_PAUSE)) {
    return
  }

  commit('SET_CALL_BUYING_LIMITS', { ...NO_CAMPAIGN_PAUSE })

  dispatch('applyCallBuyingLimits')
}

function _isHoldingCall(state) {
  return Boolean(state.activeCall || state.incomingCall)
}

function _clearCampaignBudgetPoll() {
  if (campaignBudgetPollTimerId) {
    clearInterval(campaignBudgetPollTimerId)
    campaignBudgetPollTimerId = null
  }
}

function _ensureChannel() {
  if (!channel) {
    channel = createSoftphoneChannel()
  }

  return channel
}

/**
 * Gives up everything this tab holds at the tab level: the ownership lock (or its place in the
 * queue for it) and the channel. Owner and mirror alike, and nothing here touches a call or the
 * server - see `teardownSoftphone` for what comes before it in the owner.
 */
function _disposeTabResources() {
  if (ownership) {
    ownership.destroy()
    ownership = null
  }

  if (channel) {
    channel.close()
    channel = null
    areChannelHandlersRegistered = false
  }
}

/**
 * Drops the transport without waiting for it. The unload path cannot wait: the Telnyx disconnect
 * settles on a socket event, and a page in `pagehide` is gone before that arrives. Listeners go
 * first so the disconnect's own UNREGISTERED cannot land in a store that has already been reset.
 */
function _dropTransportWithoutWaiting() {
  if (!transport) {
    return
  }

  const currentTransport = transport

  transport = null

  currentTransport.removeAllListeners()
  removeSoftphoneDevHook()

  try {
    Promise.resolve(currentTransport.disconnect()).catch(error => {
      console.error('Failed to disconnect softphone transport', error)
    })
  } catch (error) {
    console.error('Failed to disconnect softphone transport', error)
  }
}

/**
 * Points the current transport's events at the store. Shared by the go-live that builds the first
 * transport and by every reconnect attempt that builds a replacement, so a reconnected softphone
 * cannot end up half-wired - the failure that leaves a widget registered and deaf.
 *
 * `UNREGISTERED` is the one event whose payload is read here: `requested` tells a teardown from a
 * drop, and only the drop is reconnected from.
 */
function _wireTransportEvents({ dispatch }) {
  transport.on(SOFTPHONE_EVENTS.REGISTERED, () => dispatch('handleRegistered'))
  transport.on(SOFTPHONE_EVENTS.UNREGISTERED, payload => dispatch('handleUnregistered', payload))
  transport.on(SOFTPHONE_EVENTS.INCOMING, payload => dispatch('handleIncomingCall', payload))
  transport.on(SOFTPHONE_EVENTS.ANSWERED, payload => dispatch('handleCallAnswered', payload))
  transport.on(SOFTPHONE_EVENTS.ENDED, payload => dispatch('handleCallEnded', payload))
  transport.on(SOFTPHONE_EVENTS.ERROR, payload => dispatch('handleTransportError', payload))
  transport.on(SOFTPHONE_EVENTS.BUSY_DECLINED, payload => dispatch('reportBusyDecline', payload))
}

/**
 * Hands the agent's stored device choice to a transport that has not connected yet, which
 * remembers it and applies it as the client comes up - before the widget reports Ready, so the
 * first call is already on the chosen devices.
 *
 * Read from storage rather than from this tab's state: the choice may have been made in another tab
 * while nothing was live.
 */
async function _primeTransportAudioDevices({ commit }) {
  const target = transport

  if (!target) {
    return
  }

  const preferences = loadAudioDevicePreferences()

  commit('SET_AUDIO_DEVICES', preferences)

  await target.setAudioInputDevice(preferences.inputDeviceId)
  await target.setAudioOutputDevice(preferences.outputDeviceId)
}

/**
 * One device selection, for either direction. With no transport there is nothing to apply it to
 * yet, so it is saved for the next go-live - in any tab, since storage is shared. With the
 * softphone live in another tab it is refused: that tab holds the client and the call, and the
 * choice would only take effect there after its next reconnect.
 */
async function _selectAudioDevice({ commit, getters }, { command, deviceId, fields }) {
  if (getters.isSoftphoneInAnotherTab) {
    return failedDeviceResult(AUDIO_DEVICE_FAILURES.OTHER_TAB)
  }

  let result

  try {
    result = transport
      ? await transport[command](deviceId || null)
      : appliedDeviceResult({ isDeferred: true })
  } catch (error) {
    console.error(`The softphone ${command} failed`, error)

    result = failedDeviceResult(AUDIO_DEVICE_FAILURES.FAILED, error)
  }

  if (result.ok) {
    commit('SET_AUDIO_DEVICES', fields)
    saveAudioDevicePreferences({ ...loadAudioDevicePreferences(), ...fields })
  }

  return result
}

/**
 * Wires every cross-tab message once, guarded by the role each one belongs to.
 *
 * The guards are the point. A tab that inherits ownership keeps the mirror handlers it registered on
 * boot, and an unguarded `onState` would then let it apply a snapshot over the call state it now
 * owns - the softphone equivalent of taking dictation from itself.
 */
function _ensureChannelHandlers({ dispatch, state }) {
  _ensureChannel()

  if (areChannelHandlersRegistered) {
    return channel
  }

  areChannelHandlersRegistered = true

  channel.onState(message => {
    if (state.role !== SOFTPHONE_ROLES.MIRROR) {
      return
    }

    dispatch('applyRemoteState', message.snapshot)
  })

  channel.onClosed(() => {
    if (state.role !== SOFTPHONE_ROLES.MIRROR) {
      return
    }

    dispatch('handleOwnerClosed')
  })

  channel.onHello(() => {
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    _broadcastState(state)
  })

  channel.onCommand(message => {
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    dispatch('handleRemoteCommand', message)
  })

  return channel
}

/**
 * The single-owner lock. Created on first use rather than at import time so a tab that never has a
 * softphone user in it never asks the browser for anything.
 */
function _ensureOwnership({ dispatch }) {
  if (ownership) {
    return ownership
  }

  ownership = createSoftphoneOwnership()

  // Only ever the handover case in practice: the boot grant is returned by `request()` itself, and
  // a steal is returned by `steal()`. A handover means the previous owner's tab is gone.
  ownership.onGranted = ({ isHandover }) => {
    if (isHandover) {
      dispatch('adoptSoftphoneOwnership')
    }
  }

  ownership.onLost = () => dispatch('handleTabClaimLost')

  return ownership
}

/**
 * The offer this tab makes after inheriting a softphone that was live: "you were live for X -
 * resume?". Shaped so it can be handed straight to `goOnline`, which is why it is the campaign row
 * shape rather than the flattened `liveCampaign` it is built from.
 */
function _buildResumePrompt(liveCampaign) {
  return {
    id: liveCampaign.id,
    name: liveCampaign.name,
    category: { name: liveCampaign.categoryName || '' },
    language: { id: liveCampaign.languageId || '', name: liveCampaign.languageName || '' },
    budget: {
      minimumDurationSelected: liveCampaign.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(liveCampaign.minimumDurationInSeconds) || 0
    }
  }
}

/**
 * Everything a mirror needs to render. Deliberately a hand-built object rather than the whole state:
 * `role` must never travel, or a mirror would adopt the owner's role and believe it may answer.
 */
export function buildSoftphoneSnapshot(state) {
  return {
    status: state.status,
    liveCampaign: state.liveCampaign,
    incomingCall: state.incomingCall,
    activeCall: state.activeCall,
    wrapUpEndsAt: state.wrapUpEndsAt,
    isPausedForWrapUp: state.isPausedForWrapUp,
    isEndingWrapUp: state.isEndingWrapUp,
    isMuted: state.isMuted,
    // Travels because the mirrors render the same line: a mid-call drop is the agent's own tab in
    // one window and the tab they are watching in another. `tokenExpiresAt` deliberately does NOT
    // travel - only the owner holds a token, and only the owner may re-mint one.
    isRestoringConnection: state.isRestoringConnection,
    /**
     * Travels so the mirrors word the blocked notice the way the OWNER does.
     *
     * Every tab watches the same billing state and reaches the same ACCOUNT flags on its own, so
     * this looked redundant - but the campaign's flag is polled by the owner alone, and a tab whose
     * own balance has not landed yet has all of them false. Rendering the owner's `BUDGET_REACHED`
     * off that, it picked the campaign wording and the campaign link for a cap that was the
     * account's, and sent the agent to the wrong page until its own poll came round.
     */
    callBuyingLimits: state.callBuyingLimits,
    lastError: state.lastError
  }
}

export function _broadcastState(state) {
  if (state.role !== SOFTPHONE_ROLES.OWNER || !channel) {
    return
  }

  channel.postState(buildSoftphoneSnapshot(state))
}

const state = {
  // null until `initSoftphone` has settled the election. See SOFTPHONE_ROLES.
  role: null,

  status: SOFTPHONE_STATUS.OFFLINE,

  /**
   * The campaign this tab inherited an abandoned softphone for, offered back to the agent as
   * "you were live for X - resume?".
   *
   * Set only on a handover, and only in the tab that won it. Ownership passing to a new tab must
   * NOT quietly re-register the softphone: the agent is looking at some other tab by then, and a
   * tab they are not watching would start ringing on its own. Shaped so it can be handed straight
   * to `goOnline`, and deliberately kept out of the broadcast snapshot - it belongs to the one tab
   * that has to make the offer.
   */
  resumePrompt: null,

  // The single campaign this agent is live for. Only one at a time, by product decision.
  liveCampaign: null,

  incomingCall: null,

  activeCall: null,

  wrapUpEndsAt: null,

  // True from the moment the wrap-up pause landed on the server until the wrap-up resume did. Kept
  // separately from `status` because a second call can arrive and end while it is set, and that
  // call's end must still run the wrap-up - otherwise the campaign would stay paused forever.
  isPausedForWrapUp: false,

  /**
   * True while the wrap-up's resume is in flight. The status is still `wrapUp` for the whole round
   * trip - the resume can fail, and the widget must not say Ready before the campaign is - so this
   * is the only thing that tells "wrapping up" from "about to stop wrapping up".
   *
   * Extending is refused while it is set, and both controls go dead: the campaign is already being
   * resumed, so another minute cannot be granted, and an agent shown one and then rung anyway is
   * worse off than one told the window is over.
   */
  isEndingWrapUp: false,

  isMuted: false,

  /**
   * The agent's microphone and speaker choice - `{ inputDeviceId, inputLabel, outputDeviceId,
   * outputLabel }`, a `null` id meaning System default. Per browser, from `localStorage`, and held
   * by every tab: a choice made while offline is made in whichever tab the agent is looking at, and
   * the tab that later goes live reads it back from storage rather than from its own copy.
   *
   * Survives `RESET_SOFTPHONE`, deliberately. It describes the agent's desk, not the shift.
   */
  audioDevices: loadAudioDevicePreferences(),

  /**
   * When the Telnyx token this registration is authenticated by lapses, as the endpoint reported it
   * (`expiresAt`, decoded from the JWT's `exp`). Deliberately nullable: the endpoint returns null
   * rather than deny an agent a working token over an unreadable claim, and a widget with no expiry
   * simply has nothing to schedule - the reconnect still catches the drop when it comes.
   *
   * Owner-only, like `resumePrompt`: it describes the token THIS tab is holding, and a mirror has
   * none.
   */
  tokenExpiresAt: null,

  /**
   * The socket dropped while a call was up, so the reconnect is owed and waiting for the call to
   * end. Its own flag rather than a status, because the status must stay `ACTIVE` - the call is
   * real and the agent is on it - and the widget still has to say the connection is being restored
   * without implying the call in front of them is at risk.
   */
  isRestoringConnection: false,

  /**
   * Which of the BLOCKED_STATUSES the account's limits currently resolve to, or null when it can
   * buy calls - see `resolveSoftphoneBlockedStatus`. Fed by
   * `plugins/softphone-balance.plugin.js` from whatever last refreshed the balance.
   *
   * The resolved status rather than the two raw flags, so the precedence between them is decided in
   * exactly one place and `SET_STATUS` has one thing to look at.
   *
   * Not part of the broadcast snapshot, and it does not need to be: every tab watches the same
   * billing state and works this out for itself, and what the mirrors render is the owner's
   * `status`, which already carries the answer. Held on every tab all the same, so a tab that
   * inherits the softphone does not have to wait for the next poll to know.
   */
  blockedStatus: null,

  /**
   * The raw flags `blockedStatus` is resolved from, merged as they arrive. They come from two
   * different places on two different clocks - the account's pair from the billing module via
   * `plugins/softphone-balance.plugin.js`, the campaign's from this module's own poll - so they are
   * kept rather than replaced, and the status is re-derived from the whole set each time. Replacing
   * would let whichever landed last blank out the other's answer.
   */
  callBuyingLimits: {
    isBalanceLow: false,
    isDailyBudgetReached: false,
    isCampaignDailyBudgetReached: false,
    // The live campaign paused server-side by somebody other than this widget. Same poll, same
    // owner, same lifetime as the campaign's budget flag above.
    isCampaignPaused: false,
    isCampaignStrictlyPaused: false,
    campaignStrictPauseReason: null
  },

  lastError: null
}

/**
 * @type {import('vuex').GetterTree<typeof state>}
 */
const getters = {
  /**
   * ENTITLEMENT SEAM. Every softphone surface is gated on this getter and nothing else. It
   * delegates to `globalSettings/isSoftphoneUser`: the global SOFTPHONE_GOES_LIVE setting AND the
   * per-user `softphoneAgent` flag. Both fail closed, so the incoming-call UI is hidden unless
   * this user holds the grant themselves.
   *
   * Do NOT repoint this at `globalSettings/canViewSoftphoneCampaigns`. That getter is wider on
   * purpose - it also admits a super-admin impersonating the account, so they can read and edit
   * the account's softphone campaigns - and registering a softphone is exactly the thing
   * impersonation must not carry: the admin's browser would take the agent's SIP identity and
   * their live calls. Visibility travels with impersonation; the SIP identity does not.
   */
  isSoftphoneAvailable(state, getters, rootState, rootGetters) {
    return rootGetters['globalSettings/isSoftphoneUser'] === true
  },

  isSoftphoneOwner(state) {
    return state.role === SOFTPHONE_ROLES.OWNER
  },

  /**
   * The agent is taking calls, but in a different tab. What the header widget says instead of the
   * call controls, alongside the offer to take the softphone over here.
   */
  isSoftphoneInAnotherTab(state) {
    return state.role === SOFTPHONE_ROLES.MIRROR && ONLINE_STATUSES.includes(state.status)
  },

  /**
   * Whether taking the softphone over in this tab is offered.
   *
   * Refused while the owner has a call on it. A takeover drops the owner's transport, and WebRTC
   * media cannot move between tabs, so it would hang up on the caller - and the agent clicking it
   * has no way to know that from here.
   */
  canTakeOverSoftphone(state) {
    return state.role === SOFTPHONE_ROLES.MIRROR && !IN_CALL_STATUSES.includes(state.status)
  },

  hasSoftphoneResumePrompt(state) {
    return Boolean(state.resumePrompt)
  },


  /**
   * What the live campaign's calls are about, which is what the widget names - the campaign's own
   * name is the buyer's bookkeeping and means nothing to the agent answering.
   */
  liveCampaignCategoryName(state) {
    return state.liveCampaign?.categoryName || ''
  },

  /**
   * The language the campaign's callers speak, as the short code the agent reads at a glance - EN,
   * ES. Empty when the campaign carries none, and the widget then simply leaves it out.
   */
  liveCampaignLanguageCode(state) {
    return String(state.liveCampaign?.languageId || '').toUpperCase()
  },

  /**
   * The same language written out - English, Spanish - which is what the widget shows the agent.
   * A code is a thing to decode; the name is read. Falls back to the code for a campaign that
   * travelled here without a name on it, and is empty only when the campaign carries no language
   * at all, which the widget then simply leaves out.
   */
  liveCampaignLanguageName(state, getters) {
    return state.liveCampaign?.languageName || getters.liveCampaignLanguageCode
  },

  /**
   * How long an answered call must run before it earns. Zero for a campaign with no minimum, which
   * the widget says in words rather than reporting "0 seconds" - that reads like a value that
   * failed to load.
   */
  liveCampaignMinimumDurationInSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  isSoftphoneOnline(state) {
    return ONLINE_STATUSES.includes(state.status)
  },

  isRinging(state) {
    return state.status === SOFTPHONE_STATUS.RINGING
  },

  isCallActive(state) {
    return state.status === SOFTPHONE_STATUS.ACTIVE
  },

  isWrappingUp(state) {
    return state.status === SOFTPHONE_STATUS.WRAP_UP
  },

  /**
   * The agent is registered and their campaign is live, but the account cannot buy calls - so none
   * are being routed to them. What the widget explains in words instead of saying Ready.
   */
  isSoftphoneBlockedFromCalls(state) {
    return BLOCKED_STATUSES.includes(state.status)
  },

  /**
   * The sentence the panel shows under the badge, and the link that ends it - or null when there is
   * nothing to explain. See BLOCKED_NOTICES: each one names the person who can lift the cap, which
   * is the whole reason the two statuses are separate.
   */
  softphoneBlockedNotice(state) {
    return BLOCKED_NOTICE_BUILDERS[state.status]?.(state) || null
  },

  liveCampaignId(state) {
    return state.liveCampaign?.id || null
  },

  /**
   * How long an answered call must run before the widget shows the caller's full number; until then
   * it shows only the last four digits.
   *
   * The gate is the live campaign's MINIMUM DURATION - the point at which the call is billable, and
   * so the point at which the buyer has earned the number. A campaign without a minimum duration
   * has nothing to wait for, so `0`: the number is shown from the moment the call is answered.
   */
  callerIdRevealAfterSeconds(state) {
    if (!state.liveCampaign?.minimumDurationSelected) {
      return 0
    }

    return Math.max(0, Number(state.liveCampaign.minimumDurationInSeconds) || 0)
  },

  softphoneStatusLabel(state) {
    return getSoftphoneStatusLabel(state.status)
  }
}

/**
 * @type {import('vuex').ActionTree<typeof state>}
 */
const actions = {
  /**
   * Boots the softphone for this tab. Called once per dashboard tab, whether or not the agent ever
   * goes live - the tab has to know what the softphone is doing before it can render a header
   * badge or a campaigns list, and it has to be queued for ownership before the owner disappears.
   *
   * The lock is asked for HERE rather than in `goOnline`, because the thing that must be unique is
   * the tab holding the transport, not the call: two registered tabs would both ring.
   */
  async initSoftphone({ dispatch, rootGetters }) {
    if (rootGetters['auth/hasPermission']?.('campaigns') === false) return false
    const isOwner = await _ensureOwnership({ dispatch }).request()
    if (rootGetters['auth/hasPermission']?.('campaigns') === false) {
      await dispatch('teardownSoftphone', { isUnloading: true, skipServerPause: true })
      return false
    }

    await dispatch(isOwner ? 'becomeOwner' : 'becomeMirror')

    return isOwner
  },

  /**
   * Adopt the owner role. Ownership itself is granted by the lock, not by this - dispatching it
   * without having won the lock is a test-only shortcut.
   */
  becomeOwner({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannelHandlers({ dispatch, state })

    // Doubles as "there is an owner here now", which is what a mirror booting alongside this tab
    // needs before it can render anything but Offline.
    _broadcastState(state)
  },

  /**
   * Follow the owner. A mirror never creates a transport.
   */
  becomeMirror({ commit, dispatch, state }) {
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)

    _ensureChannelHandlers({ dispatch, state })

    // The owner may already be mid-call; ask where things stand rather than assume offline.
    channel.postHello()
  },

  /**
   * Take the softphone on in this tab, having just been given the lock for it.
   *
   * Shared by both ways ownership arrives at a tab that was mirroring: a HANDOVER, where the tab
   * that held it went away and the browser handed the lock on, and a TAKEOVER, where the agent
   * asked for the softphone here. Both leave the same thing behind - a campaign the other tab was
   * live for and no longer serving - and both account for it identically.
   *
   * The campaign is NOT resumed automatically. In the handover case ownership goes to whichever tab
   * the browser picked, which is not necessarily one the agent is looking at, and a tab that
   * started ringing by itself would drop calls the agent never knew had arrived. So the campaign is
   * paused - the other tab did not get to do it reliably, and a campaign left Active with no agent
   * behind it routes calls at nobody - and then offered back.
   */
  async adoptSoftphoneOwnership({ commit, dispatch, state }) {
    const inherited = state.liveCampaign

    commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

    _ensureChannelHandlers({ dispatch, state })

    // Pauses the campaign, tells the sibling mirrors and resets. They were watching the other tab,
    // not this one, so this is the only way they hear about it.
    await dispatch('handleOwnerGone')

    if (inherited) {
      commit('SET_RESUME_PROMPT', _buildResumePrompt(inherited))
    }
  },

  /**
   * Move the softphone to THIS tab, because the agent asked for it here. Deliberate and never
   * automatic: it takes the lock away from the owner, which drops that tab's transport.
   *
   * Guarded by `canTakeOverSoftphone` at the call site - a takeover mid-call would hang up on the
   * caller, since WebRTC media cannot follow the lock.
   */
  async takeOverSoftphone({ dispatch }) {
    const isOwner = await _ensureOwnership({ dispatch }).steal()

    if (!isOwner) {
      return false
    }

    await dispatch('adoptSoftphoneOwnership')

    return true
  },

  /**
   * Accepts the resume offer: go live again, in this tab, for the campaign the softphone was
   * inherited from. Both halves are needed - the server has to resume the campaign, and this tab
   * has to register - and the server call goes first, so a refused resume never leaves an agent
   * registered for a paused campaign.
   *
   * The offer is cleared only once the server has agreed. A refused resume leaves it on screen, so
   * the agent can try again rather than having the one way back to their campaign taken away.
   */
  async resumeSoftphoneFromPrompt({ commit, dispatch, state }) {
    const campaign = state.resumePrompt

    if (!campaign) {
      return
    }

    await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: campaign.id }, { root: true })

    commit('SET_RESUME_PROMPT', null)

    await dispatch('goOnline', campaign)
  },

  dismissSoftphoneResumePrompt({ commit }) {
    commit('SET_RESUME_PROMPT', null)
  },

  /**
   * The account's limits on buying calls, as the buyer balance endpoint last reported them. Driven
   * by `plugins/softphone-balance.plugin.js`, from whatever last refreshed the balance.
   *
   * Applied by asking for READY rather than by setting a blocked status directly, and that is what
   * makes it wait for the agent: `SET_STATUS` resolves Ready into whichever blocked status the
   * limits say, and only a widget that is ALREADY between calls is put through it here. One that is
   * ringing, on a call, or wrapping up is left exactly as it is, and picks the new answer up when it
   * next reaches Ready on its own.
   *
   * Going through Ready is also what moves the agent BETWEEN the two blocked statuses - a balance
   * topped up while the day's budget is still spent, say - because both of them are Ready statuses
   * and the commit re-resolves.
   *
   * Nothing is asked of the server, in either direction. The agent stays registered and their
   * campaign stays live: an account that cannot buy calls simply has none routed to it, so pausing
   * the campaign here would cost the agent their shift over something a top-up fixes in a minute -
   * and going live again afterwards would be theirs to remember.
   */
  setAccountCallBuyingLimits({ commit, dispatch, state }, { isBalanceLow, isDailyBudgetReached } = {}) {
    const patch = {
      isBalanceLow: isBalanceLow === true,
      isDailyBudgetReached: isDailyBudgetReached === true
    }

    // In FRONT of the commit, not behind it: the commit is what broadcasts now - see
    // `_isKnownCallBuyingLimits` - so the guard has to stop the commit, not just the status change.
    if (_isKnownCallBuyingLimits(state.callBuyingLimits, patch)) {
      return
    }

    commit('SET_CALL_BUYING_LIMITS', patch)

    dispatch('applyCallBuyingLimits')
  },

  /**
   * The LIVE campaign's own daily budget, from this module's poll. Separate from the account's pair
   * because it arrives from a different endpoint on a different clock - see `callBuyingLimits`.
   */
  setCampaignCallBuyingLimits({ commit, dispatch, state }, {
    isDailyBudgetReached,
    isPaused,
    isStrictPause,
    strictPauseReason
  } = {}) {
    const isCampaignPaused = isPaused === true
    const isCampaignStrictlyPaused = isCampaignPaused && isStrictPause === true

    const patch = {
      isCampaignDailyBudgetReached: isDailyBudgetReached === true,
      isCampaignPaused,
      isCampaignStrictlyPaused,
      campaignStrictPauseReason: isCampaignStrictlyPaused ? (strictPauseReason || null) : null
    }

    if (_isKnownCallBuyingLimits(state.callBuyingLimits, patch)) {
      return
    }

    commit('SET_CALL_BUYING_LIMITS', patch)

    dispatch('applyCallBuyingLimits')
  },

  /**
   * Moves the badge to match the limits, if it is this tab's badge to move and the widget is idle
   * enough to be moved.
   *
   * Applied by asking for READY rather than by setting a blocked status directly, and that is what
   * makes it wait for the agent: `SET_STATUS` resolves Ready into whichever blocked status the
   * limits say, and only a widget that is ALREADY between calls is put through it here. One that is
   * ringing, on a call, or wrapping up is left exactly as it is, and picks the new answer up when it
   * next reaches Ready on its own.
   *
   * Going through Ready is also what moves the agent BETWEEN blocked statuses - a balance topped up
   * while the day's budget is still spent, say - because both are Ready statuses and the commit
   * re-resolves.
   */
  applyCallBuyingLimits({ commit, state }) {
    // The status is the OWNER's to move. A mirror renders what the owner broadcasts - that is the
    // whole of what a mirror is - and one that moved its own badge would only be corrected by the
    // owner's next snapshot. The limits are recorded on every tab all the same, so a tab that
    // inherits the softphone already knows.
    if (state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    // Only a widget that is already between calls. Ringing, on a call or wrapping up is left alone
    // and picks the new answer up when it next reaches Ready on its own.
    if (!READY_STATUSES.includes(state.status)) {
      return
    }

    // What the badge SHOULD say, against what it says. Equal means nothing to do - which is the
    // common case, because both sources re-report the same answer every 45 seconds, and a commit
    // per poll would broadcast to every other tab for no change at all.
    if ((state.blockedStatus || SOFTPHONE_STATUS.READY) === state.status) {
      return
    }

    commit('SET_STATUS', SOFTPHONE_STATUS.READY)
  },

  /**
   * Starts re-reading the live campaign's own daily budget, and checks it once straight away.
   *
   * The immediate check matters as much as the repeat: the campaigns list gates Go Live on a fresh
   * read of this, but the RESUME PROMPT - the offer a tab makes after inheriting an abandoned
   * softphone - goes live without passing that gate at all.
   */
  startCampaignBudgetPoll({ dispatch }) {
    _clearCampaignBudgetPoll()

    campaignBudgetPollTimerId = setInterval(
      () => dispatch('refreshCampaignBudget'),
      CAMPAIGN_BUDGET_POLL_INTERVAL_IN_MS
    )

    return dispatch('refreshCampaignBudget')
  },

  /**
   * Best effort by construction: a budget that could not be read is not a budget that is spent, so
   * a failure leaves the last answer standing rather than blanking the badge either way.
   */
  async refreshCampaignBudget({ dispatch, state }) {
    const campaignId = state.liveCampaign?.id

    if (!campaignId || state.role !== SOFTPHONE_ROLES.OWNER) {
      return
    }

    const requestId = ++campaignBudgetRequestId

    // Taken BEFORE the read as well as after it: a wrap-up that paused the campaign and resumed it
    // again while the read was in flight leaves nothing behind to see at the end, but the server
    // may have answered from the middle of it.
    const wasHoldingCampaignPaused = _isWidgetHoldingCampaignPaused(state)

    try {
      const budgetState = await dispatch(
        'campaign/fetchBuyerCampaignBudgetState',
        campaignId,
        { root: true }
      )

      // A newer read has already been asked for, so this one is history however it turned out -
      // see `campaignBudgetRequestId`. Checked before the campaign id, because a superseded read of
      // the RIGHT campaign is the common case: the interval does not wait for the read it started.
      if (requestId !== campaignBudgetRequestId) {
        return
      }

      // The shift may have ended, or moved to another campaign, while this was in flight.
      if (state.liveCampaign?.id !== campaignId) {
        return
      }

      /**
       * A pause this widget can account for is not news, and reporting it would be wrong twice
       * over: the badge would say Paused for a wrap-up the agent is in the middle of, and the Ready
       * the wrap-up ends on would resolve to Paused too. The server's own wrap-up flag covers the
       * moment between the wrap-up pause landing and this store recording it.
       */
      const isOwnPause = budgetState?.isWrapUpPause === true
        || wasHoldingCampaignPaused
        || _isWidgetHoldingCampaignPaused(state)

      dispatch('setCampaignCallBuyingLimits', isOwnPause ? { ...budgetState, isPaused: false } : budgetState)
    } catch (error) {
      console.error('Could not re-read the campaign daily budget', error)
    }
  },

  /**
   * Gives up the lock, so a sibling tab can take the softphone over at once instead of waiting for
   * the browser to notice this one is gone.
   */
  releaseSoftphoneOwnership() {
    if (ownership) {
      ownership.release()
    }
  },

  /**
   * Diagnostics: how many tabs hold the softphone lock, and how many are queued behind it. The
   * invariant this whole module exists for, made checkable - `heldCount` is never above 1.
   */
  describeSoftphoneOwnership({ dispatch }) {
    return _ensureOwnership({ dispatch }).describe()
  },

  applyRemoteState({ commit, dispatch, state }, snapshot) {
    if (!snapshot) {
      return
    }

    const didWrapUpPauseChange = Boolean(snapshot.isPausedForWrapUp) !== Boolean(state.isPausedForWrapUp)

    // The agent stopped being live for a campaign: the widget paused it server-side, so the row
    // this tab is showing as Active is now Paused. Covers the agent going offline from the widget
    // and the widget dropping offline by itself (a lost registration, a transport error).
    const didStopBeingLive = Boolean(state.liveCampaign) && !snapshot.liveCampaign

    commit('APPLY_REMOTE_STATE', snapshot)

    // The widget paused or resumed the campaign on the server, and it has no campaigns list to
    // refresh - this tab does. Best effort by construction (the action swallows its own failure).
    if (didWrapUpPauseChange || didStopBeingLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * The owner's tab shut down CLEANLY - it has already paused the campaign, so there is nothing to
   * do but drop back to offline and make every Go Live button clickable again.
   *
   * This is also what keeps the tab that inherits the lock from offering a pointless resume: a
   * clean goodbye clears `liveCampaign` in every mirror, and `adoptSoftphoneOwnership` reads that
   * to decide whether there is anything to account for.
   */
  handleOwnerClosed({ commit, dispatch, state }) {
    const wasLive = Boolean(state.liveCampaign)

    commit('RESET_SOFTPHONE')

    // The widget paused the campaign on its way out. Only this tab has the list showing that
    // campaign as Active, so only this tab can put the badge right.
    if (wasLive) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  /**
   * The owner's tab disappeared without saying goodbye, and this tab found out by being handed the
   * lock. Reached when the tab is closed, reloaded or killed: the unload path tries to pause with a
   * keepalive request, but that is the one call that cannot be awaited or retried.
   *
   * So it pauses, because a campaign left Active with no agent behind it routes calls at nobody.
   * Pausing twice is harmless, which is what makes it safe to do here on a hunch.
   */
  async handleOwnerGone({ commit, dispatch, state }) {
    _ensureChannel()

    const wasPausedForWrapUp = state.isPausedForWrapUp

    await dispatch('pauseLiveCampaign')

    // Sibling mirrors were not handed the lock, so they only learn about it from here - and they
    // refetch their campaigns list on hearing it. Said AFTER the pause has landed, so that refetch
    // sees the row Paused; said before, it would fetch a list that still reads Active, and the
    // offline snapshot broadcast by the reset below finds nothing left to change and never triggers
    // a second one.
    channel.postClosed()

    commit('RESET_SOFTPHONE')

    // The refetch inside the pause above ran while this store still believed a wrap-up was in
    // progress, so it kept the server's wrap-up label - and the server keeps that label until the
    // campaign is resumed, which now never happens. One more refetch, made after the reset, is what
    // turns the row from "Paused (wrap-up)" into plain "Paused".
    if (wasPausedForWrapUp) {
      dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
    }
  },

  async handleRemoteCommand({ dispatch }, { name, payload } = {}) {
    if (name === SOFTPHONE_COMMANDS.GO_ONLINE) {
      await dispatch('goOnline', payload)

      return
    }

    if (name === SOFTPHONE_COMMANDS.GO_OFFLINE) {
      // Going offline, not tearing down: the tab stays open and keeps the lock, because it is still
      // the one tab allowed to take calls and the agent may go live again in a moment.
      await dispatch('goOffline')

      return
    }

    if (name === SOFTPHONE_COMMANDS.FINISH_WRAP_UP) {
      await dispatch('finishWrapUp')

      return
    }

    if (name === SOFTPHONE_COMMANDS.EXTEND_WRAP_UP) {
      dispatch('extendWrapUp')

      return
    }

    if (name === SOFTPHONE_COMMANDS.RESUME_PAUSED_CAMPAIGN) {
      await dispatch('resumePausedCampaign')
    }
  },

  /**
   * Go Live from wherever the agent clicked it.
   *
   * The softphone MOVES to the clicking tab. Anything else means the agent presses Go Live in the
   * tab they are working in and the call then rings in some other tab they may not even have on
   * screen. Moving it is safe here by construction: Go Live is only offered when nothing is live,
   * so the tab losing the lock has no transport and no call to drop.
   *
   * A mirror that cannot take the lock, or one whose view of the world says a call is up after all,
   * falls back to asking the owner. That fallback is not expected to be reached - the Go Live
   * button is dead while anything is live - and it exists because the alternative to a command
   * nobody acts on is a click that silently does nothing.
   */
  async requestGoOnline({ commit, dispatch, state }, campaign) {
    const payload = {
      id: campaign.id,
      name: campaign.name,
      // The category and the language are what the widget puts on screen. Narrowed here so the same
      // payload works whether it is handed to `goOnline` in this tab or structured-cloned to another.
      category: { name: campaign.category?.name || '' },
      language: { id: campaign.language?.id || '', name: campaign.language?.name || '' },
      // The minimum duration has to travel too: the widget gates the caller id on it (see
      // `callerIdRevealAfterSeconds`) and shows it beside the category. Narrowed to these fields
      // rather than passing the whole row, because this is structured-cloned on every go-live.
      budget: {
        minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
        minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0
      }
    }

    const canMoveHere = state.role !== SOFTPHONE_ROLES.OWNER
      && !IN_CALL_STATUSES.includes(state.status)
      && await _ensureOwnership({ dispatch }).steal()

    if (canMoveHere) {
      commit('SET_ROLE', SOFTPHONE_ROLES.OWNER)

      _ensureChannelHandlers({ dispatch, state })

      // The mirrored view belongs to the tab that just lost the lock. Cleared rather than paused:
      // the server-side go-live behind this click has already settled which campaigns are live, and
      // a pause here would take the agent offline for the campaign they are going live for.
      commit('SET_RESUME_PROMPT', null)
      commit('RESET_SOFTPHONE')
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('goOnline', payload)
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.GO_ONLINE, payload)

    return undefined
  },

  /**
   * Go Offline from wherever the agent clicked it. Unlike Go Live this never moves the lock: the
   * tab that holds the transport is the only one that can disconnect it, and it pauses the campaign
   * on its way out, so a mirror asks rather than takes.
   */
  requestGoOffline({ dispatch, state }) {
    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('goOffline')
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.GO_OFFLINE)

    return Promise.resolve()
  },

  /**
   * End the wrap-up now, from wherever the agent clicked it. The countdown is a floor on how long
   * the agent MAY have, not on how long they must take: one who is done filing the call takes the
   * next one straight away rather than watching a clock, or going offline to beat it and having to
   * go live again.
   *
   * Ends exactly as the timer's own expiry does - `finishWrapUp` resumes the campaign and puts the
   * widget back to Ready - so there is one ending to get right, not two.
   */
  requestFinishWrapUp({ dispatch, state }) {
    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return Promise.resolve()
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('finishWrapUp')
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.FINISH_WRAP_UP)

    return Promise.resolve()
  },

  /**
   * The other half: an agent who needs longer says so instead of going offline to stop the clock.
   * Asked for as many times as they like - see `extendWrapUp` for what each one buys.
   */
  requestExtendWrapUp({ dispatch, state }) {
    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return Promise.resolve()
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      dispatch('extendWrapUp')

      return Promise.resolve()
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.EXTEND_WRAP_UP)

    return Promise.resolve()
  },

  /**
   * Resume a campaign somebody else paused, from wherever the agent clicked it. The owner does the
   * work, as for every other control: it is the tab that polls the campaign, so it is the one whose
   * limits have to be cleared for the badge to move.
   */
  requestResumePausedCampaign({ dispatch, state }) {
    if (state.status !== SOFTPHONE_STATUS.CAMPAIGN_PAUSED) {
      return Promise.resolve()
    }

    if (state.role === SOFTPHONE_ROLES.OWNER) {
      return dispatch('resumePausedCampaign')
    }

    _ensureChannel()

    channel.postCommand(SOFTPHONE_COMMANDS.RESUME_PAUSED_CAMPAIGN)

    return Promise.resolve()
  },

  /**
   * The registration never went anywhere, so only the server half of Go Live is needed: resume the
   * campaign, and the widget is back to Ready without the agent re-registering.
   *
   * A refused resume leaves the widget exactly where it was - still Paused, still offering the
   * button - with the server's reason on screen, so the agent can try again or go offline.
   */
  async resumePausedCampaign({ commit, dispatch, state }) {
    const campaignId = state.liveCampaign?.id

    if (!campaignId || state.status !== SOFTPHONE_STATUS.CAMPAIGN_PAUSED || isResumingPausedCampaign) {
      return
    }

    isResumingPausedCampaign = true

    try {
      await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId }, { root: true })
    } catch (error) {
      console.error('Could not resume the paused campaign', error)

      commit('SET_LAST_ERROR', error?.response?.data?.title || 'Could not resume your campaign. Try again, or go offline and live again.')

      return
    } finally {
      isResumingPausedCampaign = false
    }

    /**
     * The agent went offline while the resume was in flight - Paused counts as online, so Go
     * Offline stayed clickable. The server's go-offline skips a campaign that is still paused, so
     * if it ran first this resume has just made the campaign live behind a widget that is not
     * registered. Same race, same fix as `finishWrapUp`: pause again, which is harmless if the
     * go-offline won.
     *
     * Gone live for ANOTHER campaign in the meantime, the account-wide go-offline would pause that
     * one too; going live for it again instead pauses every other softphone campaign, this one
     * included, and leaves the one the agent chose alone.
     */
    if (state.liveCampaign?.id !== campaignId) {
      const currentCampaignId = state.liveCampaign?.id

      try {
        if (currentCampaignId) {
          await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: currentCampaignId }, { root: true })
        } else {
          await dispatch('campaign/goOfflineSoftphone', null, { root: true })
        }
      } catch (pauseError) {
        console.error('Failed to re-pause the campaign after a resume raced going offline', pauseError)
      }

      return
    }

    commit('SET_LAST_ERROR', null)

    _forgetCampaignPause({ commit, dispatch, state })
  },

  /**
   * Go live for exactly one campaign. Must be called from a user gesture: the ringtone is
   * armed here (see `ringtone.js`).
   */
  async goOnline({ commit, dispatch, state, rootGetters }, campaign) {
    if (rootGetters['auth/hasPermission']?.('campaigns') === false) throw new Error('Campaign access required')
    const generation = accessGeneration
    if (!campaign?.id) {
      throw new Error('A campaign is required to go online')
    }

    // One campaign at a time: going online anywhere else takes the agent offline first.
    if (state.liveCampaign && state.liveCampaign.id !== campaign.id) {
      await dispatch('goOffline')
    } else if (state.liveCampaign?.id === campaign.id && state.status !== SOFTPHONE_STATUS.OFFLINE && state.status !== SOFTPHONE_STATUS.ERROR) {
      return
    }

    commit('SET_LAST_ERROR', null)
    // The category, the language and the minimum duration ride along because the widget shows the
    // first two and gates the caller id on the third (see `callerIdRevealAfterSeconds`), and the tab
    // holding the softphone may never fetch campaigns itself. `name` is kept for the campaign name
    // stamped onto each call.
    commit('SET_LIVE_CAMPAIGN', {
      id: campaign.id,
      name: campaign.name,
      categoryName: campaign.category?.name || '',
      languageId: campaign.language?.id || '',
      languageName: campaign.language?.name || '',
      minimumDurationSelected: campaign.budget?.minimumDurationSelected === true,
      minimumDurationInSeconds: Number(campaign.budget?.minimumDurationInSeconds) || 0
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.CONNECTING)

    await armRingtone()
    if (generation !== accessGeneration) return

    try {
      const nextTransport = await createSoftphoneTransport()
      if (generation !== accessGeneration) {
        await nextTransport.disconnect()
        nextTransport.removeAllListeners()
        return
      }
      transport = nextTransport

      _wireTransportEvents({ dispatch })

      exposeSoftphoneDevHook(transport)

      await _primeTransportAudioDevices({ commit })
      if (generation !== accessGeneration) return

      const token = await dispatch('mintSoftphoneToken')
      if (generation !== accessGeneration) return

      await transport.connect(token)
    } catch (error) {
      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || error?.message || 'Could not start taking calls')

      throw error
    }
  },

  /**
   * Mints the Telnyx `login_token` this tab's registration is authenticated by, and remembers when
   * it lapses so it can be replaced before it does.
   *
   * Returns null for a transport that needs no token (the fake), which is not the same as failing:
   * the caller connects with null and the expiry is cleared, so nothing is scheduled.
   */
  async mintSoftphoneToken({ commit, rootGetters }) {
    if (rootGetters['auth/hasPermission']?.('campaigns') === false) {
      throw new Error('Campaign access required')
    }
    if (!transport?.requiresToken) {
      commit('SET_TOKEN_EXPIRES_AT', null)

      return null
    }

    const response = await SoftphoneAPI.getWebRtcToken(rootGetters['auth/accountId'])

    commit('SET_TOKEN_EXPIRES_AT', response?.data?.expiresAt || null)

    return response?.data?.token || null
  },

  /**
   * Replaces the token a working registration is holding, a margin before it expires.
   *
   * The point is that the common case never becomes a reconnect: the 24h token is swapped while the
   * agent is idle instead of being repaired after the socket it authenticated dropped at 03:00. It
   * goes through the same loop as a drop, because the work is the same - new token, new client, wait
   * for the registration, resume the campaign - and one path that is exercised on every drop is
   * worth more than a second one that runs once a shift.
   */
  renewSoftphoneToken({ dispatch, state }) {
    tokenRenewalTimerId = null

    // A call is on. The token outlives the margin by design, so waiting for the call is free -
    // and rebuilding the client under a caller is not. No `isRestoringConnection` here, unlike a
    // real drop: nothing is broken, and the agent has nothing to be told.
    //
    // Deferred by REASON, and that matters: the socket under this call is working, so the call's
    // own ending will arrive and no timeout is armed - but a drop landing later in the same call
    // still has to pause the campaign, and `handleUnregistered` can only tell because this says
    // what it is waiting for rather than only that it is waiting.
    if (IN_CALL_STATUSES.includes(state.status)) {
      _deferReconnect(RECONNECT_REASONS.TOKEN_RENEWAL, dispatch)

      return Promise.resolve()
    }

    if (!RECONNECTABLE_STATUSES.includes(state.status)) {
      return Promise.resolve()
    }

    return dispatch('startReconnect', { reason: RECONNECT_REASONS.TOKEN_RENEWAL })
  },

  /**
   * Schedules the re-mint above. Called once a registration is established - a token nobody is
   * registered with is not worth replacing - and cleared by every path that ends the shift.
   */
  scheduleTokenRenewal({ dispatch, state }) {
    _clearTokenRenewalTimer()

    if (!state.tokenExpiresAt) {
      return
    }

    const expiresAt = new Date(state.tokenExpiresAt).getTime()

    if (!Number.isFinite(expiresAt)) {
      return
    }

    // Already inside the margin - a token minted with less life left than the margin, or a tab that
    // was asleep through it. Zero rather than a negative delay, which `setTimeout` would fire
    // immediately anyway but less obviously.
    const delayInMs = Math.max(0, expiresAt - Date.now() - getReconnectPolicy().tokenRenewalMarginInMs)

    tokenRenewalTimerId = setTimeout(() => dispatch('renewSoftphoneToken'), delayInMs)
  },

  /**
   * Starts telling the server this agent is still there, for as long as the widget is genuinely
   * registered. Called from the same places `scheduleTokenRenewal` is - both represent "this
   * registration is real right now" - so heartbeat and token renewal share one lifecycle without
   * this action needing to know why it was called.
   *
   * A failed request is deliberately not acted on here: the server's own missed-heartbeat
   * threshold is what decides whether the agent is actually gone, and reacting to a single dropped
   * request client-side would just be a second, less-informed copy of that decision.
   */
  startHeartbeat({ rootGetters }) {
    _clearHeartbeatTimer()

    heartbeatTimerId = setInterval(() => {
      if (rootGetters['auth/hasPermission']?.('campaigns') === false) return
      SoftphoneAPI.heartbeat(rootGetters['auth/accountId']).catch(error => {
        console.error('Softphone heartbeat failed', error)
      })
    }, getHeartbeatPolicy().intervalInMs)
  },

  /**
   * Forwards the transport's BUSY_DECLINED to RingbaX, so a call the widget turned away because it
   * was already ringing or on another call is not counted towards the campaign's unanswered-call
   * auto-pause. The transport has already declined the call by the time this runs, and nothing here
   * changes widget state.
   *
   * A failed report is logged and sent to Sentry, never surfaced or retried: losing it costs one
   * unanswered-call penalty - the behaviour before this existed - and a retry would land after the
   * call's unanswered verdict has usually already been counted anyway. Returns the request so a
   * test can await it; the transport never does.
   *
   * Nothing is sent without Ringba's call id: RingbaX matches on it alone, so a report for an
   * INVITE that carried no `X-PH-ringba-uuid` header could never forgive anything.
   */
  reportBusyDecline({ state, rootGetters }, { callId, ringbaCallId = null, callerNumber = null, declinedAt, heldCallState = null, telnyxCallId = null } = {}) {
    if (rootGetters['auth/hasPermission']?.('campaigns') === false) return Promise.resolve()
    const campaignId = state.liveCampaign?.id

    if (!campaignId) {
      return Promise.resolve()
    }

    // Counted in Sentry rather than the console so the missing-header rate is visible across agents.
    // Deliberately no caller number and no header dump.
    if (!ringbaCallId) {
      Sentry.captureMessage('Softphone busy decline without X-PH-ringba-uuid', {
        level: 'warning',
        tags: { softphoneAction: 'busy decline report' },
        extra: { campaignId, telnyxCallId, heldCallState }
      })

      return Promise.resolve()
    }

    return Promise.resolve()
      .then(() => SoftphoneAPI.reportBusyDecline(rootGetters['auth/accountId'], campaignId, {
        ringbaCallId,
        callerNumber,
        declinedAt: declinedAt || new Date().toISOString(),
        heldCallState,
        telnyxCallId
      }))
      .catch(error => {
        console.error('Softphone busy-decline report failed', error)
        Sentry.captureException(error, {
          tags: { softphoneAction: 'busy decline report' },
          extra: { callId, ringbaCallId, campaignId }
        })
      })
  },

  /**
   * The bounded reconnect. Owns the four steps a lost registration needs and that nothing below the
   * store can do on its own: pause the campaign, re-mint the token, rebuild the client, and resume
   * the campaign once - and only once - the transport says it is registered again.
   *
   * The campaign stays paused for the whole run on purpose. Resuming on "we are trying" would route
   * calls at a browser that is not registered, which is the exact failure the pause exists to
   * prevent; so the pause is the first thing and the resume is the last.
   *
   * Success is NOT detected here. Every registration in the widget arrives as the transport's
   * `REGISTERED` event, and `handleRegistered` hands a reconnect's registration to
   * `finishReconnect` - which means the SDK quietly recovering the old socket during the first
   * backoff finishes this run exactly as our own rebuilt client would, and no token is minted at
   * all. The loop's job is only to keep trying until something registers or the attempts run out.
   */
  async startReconnect({ commit, dispatch, state }, { reason = RECONNECT_REASONS.SOCKET_CLOSED } = {}) {
    if (reconnectRun) {
      return
    }

    /**
     * Nothing to get back to. A shift with no live campaign, or one that dropped while the agent
     * was still pressing Go Live (`goOnline` reports that failure itself), goes where a lost
     * registration went before this change: offline, with the reason on screen.
     */
    if (!state.liveCampaign?.id || !RECONNECTABLE_STATUSES.includes(state.status)) {
      await dispatch('abandonReconnect')

      return
    }

    /**
     * A call is on: the reconnect is owed, not started, and `handleCallEnded` picks it up.
     *
     * The campaign is paused NOW even so, rather than when the loop eventually starts. The
     * registration is already gone, and a campaign left Active routes calls at a browser that
     * cannot take them - a second call CAN arrive while the agent is on one, which is exactly why
     * the wrap-up pauses mid-call too (see `pauseForWrapUp`). Waiting for the call to end would
     * leave that window open for the length of the call, which is the longest window there is.
     *
     * Pausing does not touch the call itself; it stops the next one being routed.
     *
     * Only reached for a DROP. The token re-mint defers on an active call as well, but it returns
     * before dispatching this (see `renewSoftphoneToken`) - the registration there is alive and
     * well, and pausing a working campaign to renew a token nobody is waiting on would cost the
     * agent calls for nothing.
     *
     * The wait is bounded, because the call's ending is reported over the socket that just went -
     * see `_deferReconnect`.
     */
    if (state.status === SOFTPHONE_STATUS.ACTIVE) {
      _deferReconnect(reason, dispatch)

      // The registration is gone for the length of this deferral - unlike the token-renewal defer
      // above, which returns before ever getting here because nothing is actually broken there. A
      // heartbeat that kept ticking through a real drop would tell the server the agent is fine
      // while `pauseLiveCampaign` below is a best-effort call that can itself fail silently -
      // exactly the case this feature exists to catch, and the one case it must not be fooled by
      // its own signal into missing. Restarted in `finishDeferredReconnect` on the same paths
      // `finishReconnect` restarts it on.
      //
      // Kept running under a wrap-up pause, because that reason does not apply there: the campaign
      // is already paused, so there is no drop pause for the heartbeat to hide. The server keeps
      // presence across a wrap-up, and a heartbeat stopped for the outage would have the watchdog
      // declare this widget dead 60s later - while the call is still up and the tab is fine.
      if (!state.isPausedForWrapUp) {
        _clearHeartbeatTimer()
      }

      commit('SET_RESTORING_CONNECTION', true)

      // Not when the campaign is already paused for a wrap-up: that pause is not ours, and asking
      // for it again would report the agent as having walked away rather than writing up a call.
      if (!state.isPausedForWrapUp) {
        await dispatch('pauseLiveCampaign')
      }

      return
    }

    /**
     * A call that was RINGING cannot be answered any more - the answer travels over the socket that
     * just went - so it is cleared rather than left on screen. An agent reaching for Pick up on a
     * call that can no longer be picked up is worse than one told the phone is reconnecting.
     */
    if (state.incomingCall) {
      stopRinging()
      commit('SET_INCOMING_CALL', null)
    }

    /**
     * A wrap-up pause is not ours to lift. The agent earned that break and the wrap-up owns the
     * resume that ends it, so this run restores the wrap-up rather than resuming the campaign - see
     * `finishReconnect`.
     */
    const resumeOnSuccess = !state.isPausedForWrapUp

    const run = {
      attempt: 0,
      isCancelled: false,
      hasFinished: false,
      resumeOnSuccess,
      restoreStatus: resumeOnSuccess ? SOFTPHONE_STATUS.READY : SOFTPHONE_STATUS.WRAP_UP,
      timerId: null,
      resolveWait: null
    }

    reconnectRun = run

    _clearTokenRenewalTimer()

    // Same reasoning as the deferral above: a run restoring a wrap-up has no pause of its own to
    // hide, and the loop can outlast the server's 60s missed-heartbeat threshold on its own. A run
    // that gives up still stops it, through `goOffline`.
    if (resumeOnSuccess) {
      _clearHeartbeatTimer()
    }

    commit('SET_RESTORING_CONNECTION', false)
    commit('SET_LAST_ERROR', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.RECONNECTING)

    if (resumeOnSuccess) {
      // Best effort by construction (`pauseLiveCampaign` swallows its own failure). A pause that
      // failed is not a reason to abandon the registration - the agent would then be neither
      // reconnected nor paused.
      //
      // Asked for unconditionally, which means a run that was deferred behind a call pauses a
      // campaign the deferral already paused. Deliberate: pausing twice is harmless, and the
      // alternative is tracking whether a pause landed across an await and a call ending, for
      // nothing but one saved request.
      await dispatch('pauseLiveCampaign')
    }

    if (run.isCancelled) {
      return
    }

    const { maxAttempts, registrationTimeoutInMs } = getReconnectPolicy()

    while (!run.isCancelled && run.attempt < maxAttempts) {
      run.attempt += 1

      /**
       * The wait before the attempt, not after it. The first one is what gives the SDK's own
       * recovery room to land - it reconnects the same session without a new token, and a run that
       * rebuilt the client immediately would throw that away - and a token being replaced on
       * schedule has nothing to wait for.
       */
      const isImmediate = reason === RECONNECT_REASONS.TOKEN_RENEWAL && run.attempt === 1

      await _waitForReconnectDelay(run, isImmediate ? 0 : getReconnectDelayInMs(run.attempt))

      if (run.isCancelled) {
        return
      }

      try {
        await dispatch('rebuildTransportForReconnect')

        if (run.isCancelled) {
          return
        }

        const token = await dispatch('mintSoftphoneToken')

        if (run.isCancelled) {
          return
        }

        await transport.connect(token)
      } catch (error) {
        console.error(`Softphone reconnect attempt ${run.attempt} failed`, error)

        if (_isSessionGoneError(error)) {
          break
        }

        continue
      }

      if (run.isCancelled) {
        return
      }

      /**
       * Connected is not registered. `connect()` settles on the socket and the registration arrives
       * as an event afterwards, so the run waits for `finishReconnect` to cancel it; a socket that
       * never registers gives way to the next attempt instead of hanging the loop.
       */
      await _waitForReconnectDelay(run, registrationTimeoutInMs)
    }

    if (run.isCancelled) {
      return
    }

    reconnectRun = null

    await dispatch('abandonReconnect')
  },

  /**
   * Builds the replacement transport for one attempt, having disposed of the one that stopped
   * working.
   *
   * The old transport's listeners come off BEFORE its disconnect, as everywhere else in this module:
   * `disconnect()` emits UNREGISTERED, and an UNREGISTERED of our own making landing in
   * `handleUnregistered` mid-reconnect would read as a fresh drop.
   */
  async rebuildTransportForReconnect({ commit, dispatch }) {
    if (transport) {
      const previous = transport

      transport = null

      previous.removeAllListeners()

      try {
        await previous.disconnect()
      } catch (error) {
        console.error('Failed to disconnect the softphone transport before reconnecting', error)
      }
    }

    // The run was cancelled while the old transport was being disposed of - the agent went offline,
    // the tab lost the lock, the page is unloading. Building the replacement now would leave a live
    // client behind a widget that believes it has none.
    if (!reconnectRun) {
      return
    }

    const nextTransport = await createSoftphoneTransport()

    /**
     * And again after the build, which is its own await: the factory loads the Telnyx chunk with a
     * dynamic `import()`, so a Go Offline, a tab handover or an unload can land inside it.
     *
     * A transport assigned after that point is one nothing will ever dispose of - the teardown that
     * cancelled the run has already looked at `transport`, found the null left above, and gone. The
     * loop returns on its own next `isCancelled` check without touching it, so the store would be
     * OFFLINE while still holding a wired client and publishing it on the dev hook.
     */
    if (!reconnectRun) {
      try {
        await nextTransport.disconnect()
      } catch (error) {
        console.error('Failed to dispose of a softphone transport built after the reconnect was cancelled', error)
      }

      return
    }

    transport = nextTransport

    _wireTransportEvents({ dispatch })

    exposeSoftphoneDevHook(transport)

    // A rebuilt client is a new client: without this a reconnect would quietly put the agent back on
    // the system default devices mid-shift.
    await _primeTransportAudioDevices({ commit })
  },

  /**
   * A registration landed during a reconnect - ours, or the SDK's own recovery of the old socket.
   * The end of the run either way: stop trying, resume the campaign, and put the widget back where
   * the drop found it.
   *
   * `hasFinished` is what makes the resume exactly once. Two registrations in a row are ordinary
   * (a re-register, a recovered socket that then registers again), and a second resume is not a
   * wasted request: `goLive` pauses the agent's OTHER campaigns and resumes this one, so a resume
   * that lands after the agent has moved on is a campaign going live behind their back.
   */
  async finishReconnect({ commit, dispatch, state }) {
    const run = reconnectRun

    if (!run || run.hasFinished) {
      return
    }

    run.hasFinished = true

    _cancelReconnect()

    if (!run.resumeOnSuccess) {
      commit('SET_STATUS', run.restoreStatus)

      // As on every other success path. `startReconnect` clears the renewal timer for the duration
      // of the run, so a run that ends here without rearming it would leave the rest of the shift
      // with no re-mint scheduled - and the token left to expire is the failure the renewal exists
      // to prevent. Same reasoning for the heartbeat: this branch never called `goLive` (the wrap-up
      // pause was already in place before the drop), so nothing else will restart it.
      dispatch('scheduleTokenRenewal')
      dispatch('startHeartbeat')

      /**
       * The wrap-up outlived the reconnect. Its timer kept running through the outage, fired while
       * the status was `RECONNECTING`, and `finishWrapUp` bailed out because the status was not
       * `WRAP_UP` - leaving a paused campaign nothing would ever resume. Closing it out here is the
       * only place that can tell.
       */
      if (!wrapUpTimerId || (state.wrapUpEndsAt || 0) <= Date.now()) {
        dispatch('finishWrapUp')
      }

      return
    }

    try {
      await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('Reconnected, but could not resume the campaign', error)

      // Registered with a paused campaign is the one state an agent cannot detect from the widget -
      // it says Ready and the calls never come - so it is handled like a failed wrap-up resume:
      // offline, with the reason on screen and every Go Live button back.
      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || 'The connection came back but your campaign could not be resumed. Go live again to keep taking calls.')

      return
    }

    _forgetCampaignPause({ commit, dispatch, state })

    // A call that was already being routed can land while the resume is in flight; it is being
    // handled, and the status it set is the truthful one.
    if (IN_CALL_STATUSES.includes(state.status)) {
      dispatch('scheduleTokenRenewal')
      dispatch('startHeartbeat')

      return
    }

    if (state.status === SOFTPHONE_STATUS.RECONNECTING) {
      commit('SET_STATUS', run.restoreStatus)
    }

    dispatch('scheduleTokenRenewal')
    dispatch('startHeartbeat')
  },

  /**
   * The end of a reconnect that never had to run: the registration came back by itself while the
   * loop was still waiting for a call to end.
   *
   * All that is owed is the campaign the drop paused - the client is registered, so rebuilding it
   * would drop the call to fix something that is already working. The wrap-up's pause is the one
   * exception, as everywhere else: it is not this run's to lift.
   */
  async finishDeferredReconnect({ commit, dispatch, state }) {
    if (deferredReconnectReason !== RECONNECT_REASONS.SOCKET_CLOSED) {
      return
    }

    // Cleared first, so a second registration - a re-register, a recovery that registers twice -
    // cannot send a second resume. `goLive` pauses the agent's OTHER campaigns, so a duplicate is
    // not a wasted request but a campaign going live behind their back.
    _clearDeferredReconnect()

    if (state.isPausedForWrapUp) {
      commit('SET_RESTORING_CONNECTION', false)
      commit('SET_LAST_ERROR', null)
      dispatch('startHeartbeat')

      return
    }

    /**
     * The call earned a wrap-up while the connection was down, and the pause that should have
     * started it never reached the server.
     *
     * Asked again rather than resumed: the campaign is paused already - the drop paused it - so
     * resuming and re-pausing would be two requests to arrive back where it stands, and the second
     * of them could lose the race and leave the campaign live behind an agent on a break. The
     * wrap-up pause hits the same endpoint with the reason that makes the audit note say wrap-up,
     * and it is what sets the flag the hang-up reads.
     */
    if (isWrapUpPauseOwed) {
      await dispatch('pauseForWrapUp')

      commit('SET_RESTORING_CONNECTION', false)

      if (!isWrapUpPauseOwed) {
        commit('SET_LAST_ERROR', null)
      }

      // The registration itself recovered regardless of whether the wrap-up pause request landed -
      // that pause targets the campaign, not the socket - so presence should resume being tracked
      // either way.
      dispatch('startHeartbeat')

      return
    }

    try {
      await dispatch('campaign/goLiveSoftphoneCampaign', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('The connection came back under a live call, but the campaign could not be resumed', error)

      /**
       * A paused campaign behind a widget that says everything is fine is the one state an agent
       * cannot see, so the work goes back where it was rather than being dropped: still on the
       * call, the deferral is restored and the call's end runs the full loop, which either resumes
       * the campaign or lands on the give-up state. Off the call already - it ended while this
       * resume was in flight - and that loop can run now.
       */
      if (IN_CALL_STATUSES.includes(state.status)) {
        _deferReconnect(RECONNECT_REASONS.SOCKET_CLOSED, dispatch)

        return
      }

      await dispatch('startReconnect', { reason: RECONNECT_REASONS.SOCKET_CLOSED })

      return
    }

    _forgetCampaignPause({ commit, dispatch, state })

    commit('SET_RESTORING_CONNECTION', false)
    commit('SET_LAST_ERROR', null)
    dispatch('startHeartbeat')
  },

  /**
   * Where a reconnect ends when it cannot be made to work: exactly where a lost registration landed
   * before any of this existed - offline, campaign paused, and the reason on screen. Deliberately
   * no new dead end; the change can only improve on the old behaviour, never invent a worse one.
   */
  async abandonReconnect({ commit, dispatch }) {
    _cancelReconnect()

    await dispatch('goOffline')

    // After goOffline, which clears the error with the shift it belonged to.
    commit('SET_LAST_ERROR', 'The connection was lost, so your campaign has been paused. Go live again when you are ready to take calls.')
  },

  /**
   * Going offline is the OWNER's job end to end, including pausing the campaign. The tab that
   * resumed it may be gone by now - the owner's tab closing has to leave the campaign paused
   * whether or not anything else is still open.
   */
  async goOffline({ commit, dispatch, state }, { isUnloading = false } = {}) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    _cancelReconnect()
    _clearTokenRenewalTimer()
    _clearHeartbeatTimer()
    _clearCampaignBudgetPoll()
    stopRinging()

    isGoingOffline = true

    // Read before the pause, which resets it - see the second refetch at the end.
    const wasPausedForWrapUp = state.isPausedForWrapUp

    try {
      await dispatch('pauseLiveCampaign', { isUnloading })

      if (transport) {
        const currentTransport = transport

        transport = null

        try {
          await currentTransport.disconnect()
        } catch (error) {
          console.error('Failed to disconnect softphone transport', error)
        }

        currentTransport.removeAllListeners()
        removeSoftphoneDevHook()
      }

      // The ownership lock is NOT released here. Going offline leaves this tab open and still the
      // one allowed to take calls; the lock is held until the tab itself goes away. Releasing it
      // would hand the softphone to whichever tab happens to be queued next, for no reason.
      commit('RESET_SOFTPHONE')

      /**
       * The last error goes with the shift it belonged to. Every failure path sets its reason AFTER
       * calling this action - a lost registration, a transport error, a wrap-up resume that failed -
       * so clearing it here cannot swallow one; what it clears is the reason for a shift that has
       * already ended.
       *
       * It matters because an error is one of the two things that keep the header's toggle alive
       * while the status says Offline (see `canOpenPanel`). Left set, a shift the agent ended by
       * hand would go on offering them a panel with a stale explanation in it.
       */
      commit('SET_LAST_ERROR', null)

      /**
       * Going offline mid wrap-up: the row has to stop reading "Paused (wrap-up)" and read plain
       * "Paused", because the wrap-up is over - the agent ended it.
       *
       * It takes a SECOND refetch. The one inside the pause above ran while this store still
       * believed a wrap-up was in progress, so it kept the server's wrap-up label - and the server
       * keeps that label until the campaign is resumed, which now never happens (go-offline only
       * touches campaigns that are still Active, and this one is already paused). Made after the
       * reset, so `isCampaignPausedForWrapUp` finds no wrap-up to honour. Same reason and the same
       * remedy as `handleOwnerGone`.
       *
       * Not on the unload path: that tab is going away, and its pause is a keepalive request with
       * nothing left to render the answer.
       */
      if (wasPausedForWrapUp && !isUnloading) {
        dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })
      }
    } finally {
      isGoingOffline = false
    }
  },

  /**
   * Tells the server the agent is offline, which pauses EVERY softphone campaign ringing them - not
   * just the one this window went live for - so calls stop being routed to a destination with nobody
   * behind it. Best effort: a failure here must not stop the transport from disconnecting, because
   * leaving the agent connected-but-offline is worse than a campaign that stays active.
   *
   * Skipped when this window never went live: it then has nothing to take offline, and a widget that
   * merely opened and closed must not pause campaigns an agent is live for elsewhere.
   */
  async pauseLiveCampaign({ dispatch, state, rootGetters }, { isUnloading = false } = {}) {
    if (!state.liveCampaign?.id) {
      return
    }

    const accountId = rootGetters['auth/accountId']

    // An XHR started in `beforeunload` is cancelled with the page, so the unload path uses fetch
    // with keepalive instead. It skips the axios auth-refresh interceptor - acceptable, because the
    // alternative is not pausing at all, and an expired token means the agent's session was already
    // over. Every other path keeps the interceptor.
    if (isUnloading) {
      goOfflineWithKeepalive(accountId, rootGetters['auth/accessToken'])

      return
    }

    try {
      // The root campaign action rather than SoftphoneAPI directly: importing the api module here
      // creates a cycle (api -> httpClient -> router -> store -> this module) that leaves the store
      // half-built at import time.
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (error) {
      console.error('Failed to take the softphone agent offline', error)
    }
  },

  /**
   * Full teardown for a tab that is going away - closed, reloaded or logged out. Losing the tab
   * counts as going offline, but only in the OWNER: a mirror renders the owner's campaign without
   * holding it, and a mirror that paused on its way out would take the agent offline in the tab
   * that is still on the call. A mirror just leaves the queue for the lock and closes its channel.
   *
   * `isUnloading` is the path that cannot wait for anything - `pagehide`, and a logout whose
   * session may already be dead. It pauses with a keepalive request, says goodbye, releases the lock
   * and only then drops the transport, without awaiting it: the Telnyx disconnect settles on a
   * socket event, and a page that is unloading is gone before that arrives. Awaiting it left the
   * goodbye and the release unsent, so every close of a live tab looked to its siblings like a
   * crash - a second pause and a "take calls from here?" the agent had not earned.
   *
   * The ordering of pause, goodbye, release carries meaning. A `closed` message means this tab
   * paused and there is nothing left to do; a tab that vanishes WITHOUT one leaves the mirrors
   * holding its last state, and whichever of them inherits the lock pauses on its behalf (see
   * `handleOwnerGone`). Announcing before pausing would make the two indistinguishable.
   *
   * Re-entrant: a second call while one is in flight joins it. A logout can arrive mid-teardown
   * when the pause request itself fails to authenticate.
   */
  teardownSoftphone({ commit, dispatch, state }, { isUnloading = false, skipServerPause = false } = {}) {
    accessGeneration++
    if (teardownPromise) {
      return teardownPromise
    }

    const run = async () => {
      // Captured up front: a logout resets the whole store while this is in flight.
      const wasOwner = state.role === SOFTPHONE_ROLES.OWNER

      // Before the role check, and unconditionally: the tab is going away, and a reconnect that
      // outlived it would mint a token for a shift with no browser behind it. `goOffline` on the
      // deliberate path cancels it too; the unload path never reaches that.
      _cancelReconnect()
      _clearTokenRenewalTimer()
      _clearHeartbeatTimer()
      _clearCampaignBudgetPoll()

      if (!wasOwner) {
        _disposeTabResources()
        commit('SET_ROLE', null)

        return
      }

      if (!isUnloading) {
        await dispatch('goOffline')

        if (channel) {
          channel.postClosed()
        }

        _disposeTabResources()
        commit('SET_ROLE', null)

        return
      }

      clearWrapUpTimer()
      clearWrapUpEligibilityTimer()
      stopRinging()

      // Synchronous in effect: the keepalive request is handed to the browser before this returns.
      if (!skipServerPause) await dispatch('pauseLiveCampaign', { isUnloading: true })

      if (channel) {
        channel.postClosed()
      }

      commit('RESET_SOFTPHONE')

      _disposeTabResources()
      _dropTransportWithoutWaiting()

      // Neither role now. Cleared last so the reset above still went out as the owner's word, and so
      // a second teardown - a logout's safety net after the deliberate one - finds nothing to do.
      commit('SET_ROLE', null)
    }

    teardownPromise = run().finally(() => {
      teardownPromise = null
    })

    return teardownPromise
  },

  async answerCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.answer(state.incomingCall.id)
  },

  async declineCall({ state }) {
    if (!state.incomingCall || !transport) {
      return
    }

    await transport.reject(state.incomingCall.id)
  },

  async hangUp({ state }) {
    if (!state.activeCall || !transport) {
      return
    }

    await transport.hangup(state.activeCall.id)
  },

  /**
   * The agent picked a microphone. Resolves to the transport's result (see `audioDevices.js`) so the
   * widget can say what happened - applied, saved for the next go-live, applied without echo
   * cancellation, or not applied and why - and never throws.
   *
   * Saved only once it took. A choice the browser refused is not one to re-apply on the next shift.
   */
  selectAudioInputDevice({ commit, getters, state }, { deviceId = null, label = '' } = {}) {
    return _selectAudioDevice({ commit, getters, state }, {
      command: 'setAudioInputDevice',
      deviceId,
      fields: { inputDeviceId: deviceId || null, inputLabel: deviceId ? label : '' }
    })
  },

  selectAudioOutputDevice({ commit, getters, state }, { deviceId = null, label = '' } = {}) {
    return _selectAudioDevice({ commit, getters, state }, {
      command: 'setAudioOutputDevice',
      deviceId,
      fields: { outputDeviceId: deviceId || null, outputLabel: deviceId ? label : '' }
    })
  },

  async setMuted({ commit }, isMuted) {
    if (transport) {
      await transport.setMuted(isMuted)
    }

    commit('SET_MUTED', isMuted)
  },

  /**
   * Every registration in the widget arrives here, which is why the reconnect does not detect its
   * own success: whether the client was rebuilt by an attempt or the SDK quietly recovered the old
   * socket during the first backoff, this is where it lands.
   */
  handleRegistered({ commit, dispatch, state }) {
    if (reconnectRun) {
      return dispatch('finishReconnect')
    }

    /**
     * A registration arriving on a reconnect that is only OWED, not running. The common shape of
     * this is the one the loop never gets to see: the socket dropped mid-call, so the run was
     * deferred behind the caller, and then the SDK's own recovery got the session back on its own.
     *
     * Nothing was left to reconnect - but the campaign the drop paused was still paused and the
     * widget still said the connection was being restored, for the rest of the call and a whole
     * needless rebuild afterwards.
     */
    if (deferredReconnectReason === RECONNECT_REASONS.SOCKET_CLOSED) {
      return dispatch('finishDeferredReconnect')
    }

    if (state.status === SOFTPHONE_STATUS.CONNECTING) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      dispatch('scheduleTokenRenewal')
      dispatch('startHeartbeat')

      // Here rather than in `goOnline`, alongside the token renewal and for the same reason: a
      // shift that never registered has no campaign to watch the budget of. The first read happens
      // at once, which is what catches a resume-prompt go-live - that path never passes the
      // campaigns list's own gate.
      dispatch('startCampaignBudgetPoll')
    }

    return undefined
  },

  /**
   * The registration is gone, so Telnyx has nowhere to send a call - but the campaign is still
   * live, and Ringba would keep routing calls at a destination that cannot answer them.
   *
   * Which of the two things happened decides everything:
   *
   * - REQUESTED. This event belongs to a teardown of ours, and that teardown sets the status
   *   itself. Nothing to do.
   * - A DROP. A laptop lid, a Wi-Fi hop, a VPN reconnect, a proxy idle timeout, a token that
   *   outlived the shift. None of those is worth an agent's shift, so the widget gets the
   *   registration back instead of ending it - and only when that fails does it land where it used
   *   to land immediately (offline, campaign paused, the reason on screen).
   */
  async handleUnregistered({ commit, dispatch, state }, { requested = false } = {}) {
    if (state.status === SOFTPHONE_STATUS.OFFLINE) {
      return
    }

    // `isGoingOffline` as well as the flag on the event: the transports report a teardown of their
    // own accurately, but an older transport, or one that dropped as the teardown began, does not
    // have to be trusted to for the store to behave.
    if (isGoingOffline || requested) {
      commit('SET_STATUS', SOFTPHONE_STATUS.OFFLINE)

      return
    }

    // A drop while a reconnect is already in flight, or a drop already owed, belongs to that run.
    // Starting a second loop would double the attempts and race two clients at the same SIP
    // identity.
    //
    // A TOKEN_RENEWAL deferral is NOT that. It was owed behind a call on a socket that was working,
    // and this is that socket going: falling through upgrades it to a drop, which is what pauses
    // the campaign and tells the agent. Swallowing it here would leave the campaign Active at
    // Ringba behind an unregistered browser for the rest of the call - the longest window there is,
    // and the one the pause exists to close.
    if (reconnectRun || deferredReconnectReason === RECONNECT_REASONS.SOCKET_CLOSED) {
      return
    }

    await dispatch('startReconnect', { reason: RECONNECT_REASONS.SOCKET_CLOSED })
  },

  /**
   * A call can still land during wrap-up: one that was already being routed when the campaign
   * paused. It is taken like any other, and `isPausedForWrapUp` stays set so its end runs the
   * wrap-up again and the campaign is eventually resumed.
   */
  handleIncomingCall({ commit, state }, { callId, campaignName, callerNumber } = {}) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', null)
    commit('SET_INCOMING_CALL', {
      id: callId,
      // v1 shows the campaign and nothing else; the transport has no campaign of its own.
      campaignName: campaignName || state.liveCampaign?.name || '',
      // Carried but deliberately NOT shown while ringing - the widget only reveals it on an
      // answered call. See `callerId` in SoftphoneWidget.
      callerNumber: callerNumber || null,
      receivedAt: Date.now()
    })
    commit('SET_STATUS', SOFTPHONE_STATUS.RINGING)

    startRinging()
  },

  /**
   * Answered ONCE per call, however many times the event arrives.
   *
   * The SDK re-emits `active` for a call it re-synced - which is what a socket recovered under a
   * live call does, because `keepConnectionAliveOnSocketClose` keeps the call itself up - and the
   * call was never re-answered. Treating that as a new answer restarted the duration the agent is
   * watching AND the wrap-up eligibility clock, so a nine minute call that met a Wi-Fi blip looked
   * like a fresh one and lost the break it had already earned.
   */
  handleCallAnswered({ commit, dispatch, state }, { callId, callerNumber } = {}) {
    stopRinging()

    const id = callId || state.incomingCall?.id
    const answered = state.activeCall?.id === id ? state.activeCall : null

    commit('SET_ACTIVE_CALL', {
      id,
      campaignName: answered?.campaignName || state.incomingCall?.campaignName || state.liveCampaign?.name || '',
      // The ringing call already carried it; the answered event repeats it, so prefer whichever
      // arrived - a call picked up in another window has no local incomingCall to read from.
      callerNumber: callerNumber || answered?.callerNumber || state.incomingCall?.callerNumber || null,
      answeredAt: answered?.answeredAt || Date.now()
    })
    commit('SET_INCOMING_CALL', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.ACTIVE)

    // Everything below belongs to the answer itself. A re-sync must not reset the agent's mute -
    // the recovery keeps the media, so what they chose still holds - and must not re-arm the
    // eligibility timer, which has been running for this call all along.
    if (answered) {
      return
    }

    commit('SET_MUTED', false)

    // A call that is still up this long earns a wrap-up; the campaign is paused now, mid-call, so
    // nothing new is routed at an agent who is about to need a break. See WRAP_UP_* above.
    clearWrapUpEligibilityTimer()
    wrapUpEligibilityTimerId = setTimeout(() => dispatch('pauseForWrapUp'), WRAP_UP_ELIGIBLE_CALL_DURATION_IN_MS)
  },

  /**
   * Pauses the live campaign server-side because the current call has run long enough to earn a
   * wrap-up. Best effort: a failure leaves the call untouched and the agent told, and the call then
   * ends straight into Ready as a short one would.
   */
  async pauseForWrapUp({ commit, dispatch, state }) {
    clearWrapUpEligibilityTimer()

    if (state.status !== SOFTPHONE_STATUS.ACTIVE || state.isPausedForWrapUp || !state.liveCampaign?.id) {
      return
    }

    try {
      await dispatch('campaign/pauseSoftphoneForWrapUp', null, { root: true })
    } catch (error) {
      console.error('Failed to pause the campaign for wrap-up', error)

      commit('SET_LAST_ERROR', 'Could not pause the campaign for wrap-up - the next call may arrive as soon as this one ends')

      // Remembered rather than given up on, because the likeliest reason this failed is the one
      // the widget is already dealing with: the agent's connection is down. The break stays owed
      // and `finishDeferredReconnect` asks again once the connection is back - while the call is
      // still up, which is the only time the answer is still useful.
      isWrapUpPauseOwed = IN_CALL_STATUSES.includes(state.status)

      return
    }

    // The request was in flight for a while; the world may have moved on. An agent who went offline
    // meanwhile wanted the campaign paused anyway, and the reset already cleared this flag - setting
    // it now would leave a stale "paused for wrap-up" on an offline widget.
    if (!ONLINE_STATUSES.includes(state.status)) {
      return
    }

    commit('SET_PAUSED_FOR_WRAP_UP', true)

    // A drop earlier in this call stopped the heartbeat, because back then the campaign was live and
    // the drop's own pause could have failed unseen. This pause landing means the campaign is paused
    // now, so that reason is gone - and the server has just rebuilt presence for the wrap-up, which
    // the watchdog would give up on 60s later if nothing kept it fresh until the socket is back.
    if (deferredReconnectReason && heartbeatTimerId === null) {
      dispatch('startHeartbeat')
    }

    /**
     * The list is refetched HERE, after the flag, and that ordering is the whole point.
     *
     * "Paused (wrap-up)" is resolved as each row arrives, against this store's own state - see
     * `isCampaignPausedForWrapUp`. A refetch made before the flag lands strips the label instead of
     * showing it, which is exactly what a refetch inside `pauseSoftphoneForWrapUp` used to do.
     *
     * It never showed while the softphone lived in a window of its own: that window had no list, so
     * its refetch was a no-op and the dashboard tab picked the label up from the broadcast below.
     * The owning tab now has the list in front of the agent and has to refresh it itself - the
     * broadcast only ever reaches the OTHER tabs.
     */
    dispatch('campaign/refreshBuyerCampaignsIfLoaded', null, { root: true })

    // The call ended while the pause was in flight and handleCallEnded, seeing no pause yet, went
    // back to Ready. The campaign IS paused now, so the wrap-up has to start here or nothing would
    // ever resume it - and that holds just as much for a call that ended into `BALANCE_TOO_LOW`,
    // where the campaign is paused behind an agent who has funds coming and a campaign nothing
    // would put back.
    if (READY_STATUSES.includes(state.status)) {
      dispatch('beginWrapUp')
    }
  },

  /**
   * Ended ONCE per call, however many times the ending arrives.
   *
   * Telnyx reports it twice - `hangup` and then `destroy`, both of which the transport maps to
   * ENDED - and running the teardown again restarts the wrap-up window, because `beginWrapUp` sets
   * a fresh deadline from now. Observed at 0.4s between the two, so the agent quietly loses
   * whatever `destroy` trails `hangup` by off their break.
   *
   * The id is taken from the state when the event carries none, so the deferral's own timeout
   * (which settles a call whose ending can no longer be reported) claims the same call and the
   * real ENDED that may still arrive afterwards is recognised as the repeat it is.
   */
  handleCallEnded({ commit, dispatch, state }, { callId } = {}) {
    // An ending for some OTHER call - one the transport turned away as busy - is not this one's.
    // Acting on it would clear the agent's live call from the widget and drop them to Ready
    // mid-conversation. The transport already filters these; this is the store's own guard.
    if (callId && _isHoldingCall(state) && callId !== state.activeCall?.id && callId !== state.incomingCall?.id) {
      return
    }

    const endingCallId = callId || state.activeCall?.id || state.incomingCall?.id || null

    if (endingCallId && endingCallId === endedCallId) {
      return
    }

    endedCallId = endingCallId

    stopRinging()
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()

    commit('SET_INCOMING_CALL', null)
    commit('SET_ACTIVE_CALL', null)
    commit('SET_MUTED', false)

    if (state.isPausedForWrapUp) {
      dispatch('beginWrapUp')
    } else if (IN_CALL_STATUSES.includes(state.status) || state.status === SOFTPHONE_STATUS.WRAP_UP) {
      // A short call, a decline, a missed call: the campaign was never paused, so the agent is
      // simply eligible for the next call. Anything else (offline, error, another tab) is left as
      // it is.
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)
    }

    /**
     * The socket dropped while this call was up, or the token came due, and the reconnect waited
     * for the call to finish - see `handleUnregistered` and `renewSoftphoneToken`. Started here,
     * after the call's own ending has been settled, so the state the run restores on success is
     * the one the call left behind (Ready, or the wrap-up it just earned).
     */
    if (deferredReconnectReason) {
      // The reason is carried through rather than assumed to be a drop: a token being replaced on
      // schedule has nothing to wait for and starts its first attempt at once, while a drop takes
      // the first backoff - which is also the window the SDK's own recovery lands in.
      const reason = deferredReconnectReason

      _clearDeferredReconnect()

      commit('SET_RESTORING_CONNECTION', false)

      dispatch('startReconnect', { reason })
    }
  },

  beginWrapUp({ commit, dispatch }) {
    clearWrapUpTimer()

    commit('SET_WRAP_UP_ENDS_AT', Date.now() + WRAP_UP_DURATION_IN_MS)
    commit('SET_STATUS', SOFTPHONE_STATUS.WRAP_UP)

    wrapUpTimerId = setTimeout(() => dispatch('finishWrapUp'), WRAP_UP_DURATION_IN_MS)
  },

  /**
   * Another WRAP_UP_DURATION_IN_MS, as many times as the agent asks for it. Added to the END of the
   * window rather than restarting it, so an agent who asks with 40s still on the clock is given 100s
   * and never loses time by asking for more.
   *
   * The campaign is already paused - nothing goes to the server here, only the deadline this store
   * resumes on moves, and the broadcast carries the new one to every other tab's countdown.
   */
  extendWrapUp({ commit, dispatch, state }) {
    // `isEndingWrapUp` rather than the status: the status is still `wrapUp` while the resume that
    // ends the window is in flight, and a minute granted there is a minute the agent does not get -
    // the resume lands a moment later and takes them back to Ready regardless.
    if (state.status !== SOFTPHONE_STATUS.WRAP_UP || state.isEndingWrapUp) {
      return
    }

    clearWrapUpTimer()

    // A deadline already in the past means the expiry is mid-flight or the tab was asleep through
    // it; extending from now rather than from that deadline is what makes the extra minute a minute.
    const wrapUpEndsAt = Math.max(state.wrapUpEndsAt || 0, Date.now()) + WRAP_UP_DURATION_IN_MS

    commit('SET_WRAP_UP_ENDS_AT', wrapUpEndsAt)

    wrapUpTimerId = setTimeout(() => dispatch('finishWrapUp'), wrapUpEndsAt - Date.now())
  },

  /**
   * End of the wrap-up window: resume the campaign the agent was live on. A resume that fails leaves
   * a paused campaign behind a Ready-looking widget, which is the one state an agent cannot detect
   * from the widget alone, so it is handled like a failed goOnline - the widget goes offline with the
   * error showing and every Go Live button comes back so the agent can go live again.
   */
  async finishWrapUp({ commit, dispatch, state }) {
    clearWrapUpTimer()

    if (state.status !== SOFTPHONE_STATUS.WRAP_UP) {
      return
    }

    /**
     * Single flight, and the status cannot say so: it stays `wrapUp` for the whole resume round
     * trip, so the expiry, a Ready click and a mirror's command can all pass the check above while
     * one resume is already out.
     *
     * A second resume is not a wasted request, it is a broken campaign. The first ends the wrap-up
     * and leaves the widget Ready; the second then returns to a store that is no longer wrapping up
     * and reads that as the agent having gone offline underneath it, which pauses the campaign -
     * behind a widget that says Ready and an agent who is waiting for calls that never come.
     */
    if (state.isEndingWrapUp) {
      return
    }

    commit('SET_WRAP_UP_ENDS_AT', null)

    if (!state.isPausedForWrapUp) {
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    // The window is over from here on, whatever the resume does: the status has to stay `wrapUp`
    // until the campaign is really live again, so this is what stops Extend granting a minute that
    // the resume landing a moment later would take straight back.
    commit('SET_ENDING_WRAP_UP', true)

    try {
      await dispatch('campaign/resumeSoftphoneAfterWrapUp', { campaignId: state.liveCampaign?.id }, { root: true })
    } catch (error) {
      console.error('Failed to resume the campaign after wrap-up', error)

      /**
       * The resume failed because the CONNECTION did, and a reconnect is already putting that
       * right. Going offline here would cancel that run - `goOffline` calls `_cancelReconnect` -
       * and cost the agent a shift that a blip was seconds away from giving back.
       *
       * This is the shape a laptop lid opening makes: the wrap-up timer expired while the machine
       * slept, so `finishWrapUp` and the dropped socket are both noticed on wake, and the resume
       * goes out over a connection that is not up yet.
       *
       * Left owed instead. `isPausedForWrapUp` is still set and the window's timer is already
       * cleared, so the run restores `WRAP_UP` and its own close-out re-runs this resume; a run
       * that cannot get the registration back still ends on `abandonReconnect`, which is this
       * same offline-with-the-reason state by another road.
       */
      if (reconnectRun || deferredReconnectReason) {
        return
      }

      await dispatch('goOffline')

      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
      commit('SET_LAST_ERROR', error?.response?.data?.title || 'Could not resume the campaign after wrap-up. Go live again to keep taking calls.')

      return
    } finally {
      commit('SET_ENDING_WRAP_UP', false)
    }

    // The resume was a Go Live, so it lifted any pause behind it too - the system's included.
    _forgetCampaignPause({ commit, dispatch, state })

    if (state.status === SOFTPHONE_STATUS.WRAP_UP) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)
      commit('SET_STATUS', SOFTPHONE_STATUS.READY)

      return
    }

    // A call landed while the resume was in flight: the campaign is live again and the call is being
    // handled; only the flag needs clearing.
    if (IN_CALL_STATUSES.includes(state.status)) {
      commit('SET_PAUSED_FOR_WRAP_UP', false)

      return
    }

    /**
     * A drop landed while the resume was in flight. Not the agent going offline: the reconnect has
     * already paused the campaign itself and it restores the wrap-up on success - which re-runs
     * this resume - so the pause below would be a spurious account-wide go-offline on top of a run
     * that is handling this.
     */
    if (state.status === SOFTPHONE_STATUS.RECONNECTING) {
      return
    }

    // The agent went offline while the resume was in flight. Their go-offline and this go-live raced
    // on the server, and if the resume landed last the campaign is Active with nobody behind it.
    // Pausing again is harmless if the pause won; it is the only fix if it lost.
    try {
      await dispatch('campaign/goOfflineSoftphone', null, { root: true })
    } catch (pauseError) {
      console.error('Failed to re-pause the campaign after a wrap-up resume raced going offline', pauseError)
    }
  },

  /**
   * A transport error can mean the registration is gone (a dead websocket, a failed re-register),
   * and the same reasoning as handleUnregistered applies: a live campaign would go on having calls
   * routed at a browser that cannot take them. The campaign is paused first, then the error is
   * restored on top of the reset so the agent is left looking at what happened.
   */
  async handleTransportError({ commit, dispatch, state }, { message } = {}) {
    stopRinging()

    /**
     * A reconnect is running or owed, and a failing attempt is expected to complain on its way
     * down. The loop is the thing that decides whether the shift survives - it has attempts left,
     * and it ends with the agent offline and the reason on screen when it does not - so an error
     * event here must not end the shift underneath it.
     */
    if (reconnectRun || deferredReconnectReason === RECONNECT_REASONS.SOCKET_CLOSED) {
      console.error('Softphone transport error while reconnecting', message)

      return
    }

    if (isGoingOffline) {
      commit('SET_LAST_ERROR', message || 'Connection error')
      commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)

      return
    }

    /**
     * The drop arrives HERE first, not at `handleUnregistered`, whenever the socket dies abruptly -
     * a laptop lid, a Wi-Fi hop, a VPN reconnect. The Telnyx SDK emits `telnyx.error` from the
     * websocket's own `onerror` and only then `telnyx.socket.close`, and the browser fires `error`
     * before `close` for every unclean drop. Ending the shift here would therefore end it for the
     * exact failures this reconnect exists for - and worse, silently: `goOffline` calls the
     * transport's `disconnect()`, so the close that follows carries `requested`, and
     * `handleUnregistered` reads a drop of the network's making as a teardown of ours.
     *
     * So an error in a state a registration can be recovered from starts the loop, and the loop
     * owns what happens next. `startReconnect` sends every other state - no live campaign, still
     * connecting - straight to `abandonReconnect`, which is the offline-with-the-reason-on-screen
     * this used to do immediately.
     */
    if (state.liveCampaign?.id && RECONNECTABLE_STATUSES.includes(state.status)) {
      console.error('Softphone transport error, reconnecting', message)

      await dispatch('startReconnect', { reason: RECONNECT_REASONS.SOCKET_CLOSED })

      return
    }

    await dispatch('goOffline')

    commit('SET_STATUS', SOFTPHONE_STATUS.ERROR)
    commit('SET_LAST_ERROR', message || 'Connection error')
  },

  /**
   * Another tab took the softphone over. Drop the transport without re-claiming, so the agent has
   * exactly one tab taking calls.
   *
   * Deliberately does NOT pause the campaign: the new owner is authoritative, and it pauses and
   * offers the campaign back itself (see `takeOverSoftphone`), so pausing here would fight it.
   *
   * This tab becomes an ordinary mirror rather than a dead one. The OTHER_TAB status is what it
   * shows until the new owner answers the hello below, which usually arrives immediately - it is
   * the visible state only when the takeover left nobody broadcasting.
   */
  async handleTabClaimLost({ commit, dispatch, state }) {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    // The new owner registers for this agent; a reconnect here would put a second client on the
    // same SIP identity and Telnyx would fork an inbound call to both.
    _cancelReconnect()
    _clearTokenRenewalTimer()
    _clearHeartbeatTimer()
    _clearCampaignBudgetPoll()
    stopRinging()

    if (transport) {
      const currentTransport = transport

      transport = null

      // Listeners go BEFORE the disconnect, not after. Both transports emit UNREGISTERED from
      // their own `disconnect()`, and `handleUnregistered` takes the agent offline - which would
      // pause the campaign the new owner may have just gone live for, and leave this window
      // showing a lost-connection error for a handover that worked.
      currentTransport.removeAllListeners()

      try {
        await currentTransport.disconnect()
      } catch (error) {
        console.error('Failed to disconnect softphone transport', error)
      }

      removeSoftphoneDevHook()
    }

    // The role goes first, so the reset below is not broadcast as this tab's last word on a
    // softphone it no longer owns.
    commit('SET_ROLE', SOFTPHONE_ROLES.MIRROR)
    commit('RESET_SOFTPHONE')
    commit('SET_RESUME_PROMPT', null)
    commit('SET_STATUS', SOFTPHONE_STATUS.OTHER_TAB)

    _ensureChannelHandlers({ dispatch, state })

    channel.postHello()
  }
}

/**
 * @type {import('vuex').MutationTree<typeof state>}
 */
const mutations = {
  SET_ROLE(state, role) {
    state.role = role
  },

  /**
   * Applies a snapshot broadcast by the owner. Deliberately does NOT touch `role`: a mirror that
   * adopted the owner's role would start believing it may answer calls.
   */
  APPLY_REMOTE_STATE(state, snapshot) {
    state.status = snapshot.status
    state.liveCampaign = snapshot.liveCampaign
    state.incomingCall = snapshot.incomingCall
    state.activeCall = snapshot.activeCall
    state.wrapUpEndsAt = snapshot.wrapUpEndsAt
    state.isPausedForWrapUp = Boolean(snapshot.isPausedForWrapUp)
    state.isEndingWrapUp = Boolean(snapshot.isEndingWrapUp)
    state.isMuted = snapshot.isMuted
    state.isRestoringConnection = Boolean(snapshot.isRestoringConnection)
    // The owner is authoritative for the badge, so it is authoritative for the sentence under it.
    // A mirror's own balance plugin may patch the account half again later, which is no worse: both
    // readings are real, and the status it renders still comes from here.
    state.callBuyingLimits = snapshot.callBuyingLimits || state.callBuyingLimits
    state.lastError = snapshot.lastError
  },

  /**
   * The one place a status becomes THE status - and the one place Ready is RESOLVED rather than
   * taken at its word: an account that cannot buy calls gets `BALANCE_TOO_LOW` instead.
   *
   * Here rather than at the handful of call sites that reach Ready - a registration landing, a call
   * ending, a wrap-up finishing, a reconnect restoring whatever the drop found - because that set
   * grows, and a single missed one is an agent sitting behind a green badge waiting for calls that
   * cannot arrive. It is the same reasoning as the sync plugin subscribing to mutations instead of
   * trusting every action to broadcast.
   *
   * It also buys the DEFERRAL for nothing. A ringing, active or wrapping-up widget is never asked
   * to be Ready, so the blocked status cannot interrupt the call in front of the agent or the
   * wrap-up that call earned: it lands on the way out of them, whenever that is.
   */
  SET_STATUS(state, status) {
    if (status === SOFTPHONE_STATUS.READY && state.blockedStatus) {
      state.status = state.blockedStatus

      return
    }

    state.status = status
  },

  /**
   * Merges whatever a source just learned into the limits, and re-derives the blocked status from
   * the merged set - never from the patch alone, which only ever describes one of the two sources.
   */
  SET_CALL_BUYING_LIMITS(state, limits) {
    state.callBuyingLimits = { ...state.callBuyingLimits, ...limits }
    state.blockedStatus = resolveSoftphoneBlockedStatus(state.callBuyingLimits)
  },

  SET_LIVE_CAMPAIGN(state, campaign) {
    state.liveCampaign = campaign
  },

  SET_INCOMING_CALL(state, call) {
    state.incomingCall = call
  },

  SET_ACTIVE_CALL(state, call) {
    state.activeCall = call
  },

  SET_WRAP_UP_ENDS_AT(state, wrapUpEndsAt) {
    state.wrapUpEndsAt = wrapUpEndsAt
  },

  SET_PAUSED_FOR_WRAP_UP(state, isPausedForWrapUp) {
    state.isPausedForWrapUp = isPausedForWrapUp
  },

  SET_ENDING_WRAP_UP(state, isEndingWrapUp) {
    state.isEndingWrapUp = isEndingWrapUp
  },

  SET_MUTED(state, isMuted) {
    state.isMuted = isMuted
  },

  SET_AUDIO_DEVICES(state, audioDevices) {
    state.audioDevices = { ...state.audioDevices, ...audioDevices }
  },

  SET_TOKEN_EXPIRES_AT(state, tokenExpiresAt) {
    state.tokenExpiresAt = tokenExpiresAt
  },

  SET_RESTORING_CONNECTION(state, isRestoringConnection) {
    state.isRestoringConnection = Boolean(isRestoringConnection)
  },

  SET_LAST_ERROR(state, lastError) {
    state.lastError = lastError
  },

  SET_RESUME_PROMPT(state, resumePrompt) {
    state.resumePrompt = resumePrompt
  },

  // `role` is deliberately untouched: resetting the call state does not stop this tab owning the
  // softphone. Neither is `resumePrompt`, which outlives the reset by construction - it is set from
  // the state this very mutation clears. `blockedStatus` is not cleared either, but re-derived: see
  // the note on `callBuyingLimits` below, which is what decides how much of it survives the shift.
  RESET_SOFTPHONE(state) {
    state.status = SOFTPHONE_STATUS.OFFLINE
    state.liveCampaign = null
    state.incomingCall = null
    state.activeCall = null
    state.wrapUpEndsAt = null
    state.isPausedForWrapUp = false
    state.isEndingWrapUp = false
    state.isMuted = false
    state.isRestoringConnection = false
    // The token went with the registration it authenticated. Left set, the next go-live would
    // schedule a re-mint against the expiry of a token nothing is holding any more.
    state.tokenExpiresAt = null
    /**
     * The CAMPAIGN's budget flag goes with the shift, unlike the account's two beside it in
     * `callBuyingLimits`: it described one campaign, and the next go-live may be for another. Left
     * set, an agent moving to a campaign with budget to spare would be told it had none.
     *
     * The account's flags are deliberately NOT cleared here - an account does not get its funds or
     * its daily budget back because an agent clocked off - so `blockedStatus` is RE-DERIVED from
     * what is left rather than nulled. An account still out of funds is still out of funds, and the
     * next go-live must not pass through a Ready that lasts until the poll comes round.
     */
    state.callBuyingLimits = {
      ...state.callBuyingLimits,
      isCampaignDailyBudgetReached: false,
      // The pause goes with the shift for the same reason: going live again resumes the campaign.
      ...NO_CAMPAIGN_PAUSE
    }
    state.blockedStatus = resolveSoftphoneBlockedStatus(state.callBuyingLimits)
  }
}

// Test seam: lets a spec inject a FakeSoftphoneTransport without going through `goOnline`.
export const __testing = {
  setTransport(nextTransport) {
    transport = nextTransport
  },

  getTransport() {
    return transport
  },

  setOwnership(nextOwnership) {
    ownership = nextOwnership
  },

  getOwnership() {
    return ownership
  },

  hasPendingWrapUpTimer() {
    return wrapUpTimerId !== null
  },

  hasPendingWrapUpEligibilityTimer() {
    return wrapUpEligibilityTimerId !== null
  },

  isWrapUpPauseOwed() {
    return isWrapUpPauseOwed
  },

  hasPendingTokenRenewalTimer() {
    return tokenRenewalTimerId !== null
  },

  isHeartbeatRunning() {
    return heartbeatTimerId !== null
  },

  isReconnecting() {
    return reconnectRun !== null
  },

  getReconnectAttempt() {
    return reconnectRun ? reconnectRun.attempt : 0
  },

  isReconnectDeferred() {
    return deferredReconnectReason !== null
  },

  getDeferredReconnectReason() {
    return deferredReconnectReason
  },

  hasPendingDeferredReconnectTimer() {
    return deferredReconnectTimerId !== null
  },

  setChannel(nextChannel) {
    channel = nextChannel
  },

  getChannel() {
    return channel
  },

  reset() {
    clearWrapUpTimer()
    clearWrapUpEligibilityTimer()
    _cancelReconnect()
    _clearTokenRenewalTimer()
    _clearHeartbeatTimer()
    _clearCampaignBudgetPoll()
    transport = null
    ownership = null
    channel = null
    areChannelHandlersRegistered = false
    teardownPromise = null
    isGoingOffline = false
    isResumingPausedCampaign = false
    endedCallId = null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
